'use client';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { SplitSide, DiffLineType, getSyntaxDiffTemplate, getSyntaxLineTemplate, getPlainDiffTemplate, getPlainLineTemplate, checkDiffLineIncludeChange, composeLen, getSplitContentLines, getUnifiedContentLine, _cacheMap, DiffFile, highlighter, multiSelectClassNames, createDiffMultiSelectManager } from '@git-diff-view/core';
export * from '@git-diff-view/core';
export { SplitSide } from '@git-diff-view/core';
import * as React from 'react';
import { useState, useEffect, useLayoutEffect, createContext, useContext, memo, useRef, Fragment as Fragment$1, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim/index.js';
import { createStore, ref } from 'reactivity-store';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __classPrivateFieldGet(receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
}

function __classPrivateFieldSet(receiver, state, value, kind, f) {
    if (typeof state === "function" ? receiver !== state || true : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (state.set(receiver, value)), value;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var _TextMeasure_instances, _TextMeasure_key, _TextMeasure_map, _TextMeasure_getInstance;
let canvasCtx = null;
const getKey = (font, text) => {
    return `${font.fontFamily}-${font.fontStyle}-${font.fontSize}-${text}`;
};
const getStableKey = (font, text) => {
    return getKey(font, "0".repeat(text.length));
};
class TextMeasure {
    constructor() {
        _TextMeasure_instances.add(this);
        _TextMeasure_key.set(this, "");
        _TextMeasure_map.set(this, {});
    }
    measure(text, font) {
        const currentKey = getStableKey(font || {}, text);
        if (__classPrivateFieldGet(this, _TextMeasure_map, "f")[currentKey]) {
            return __classPrivateFieldGet(this, _TextMeasure_map, "f")[currentKey];
        }
        const instance = __classPrivateFieldGet(this, _TextMeasure_instances, "m", _TextMeasure_getInstance).call(this);
        if (font) {
            const currentFontKey = `${font.fontFamily}-${font.fontStyle}-${font.fontSize}`;
            if (__classPrivateFieldGet(this, _TextMeasure_key, "f") !== currentFontKey) {
                __classPrivateFieldSet(this, _TextMeasure_key, currentFontKey);
                instance.font = `${font.fontStyle || ""} ${font.fontSize || ""} ${font.fontFamily || ""}`;
            }
        }
        else {
            instance.font = "";
        }
        const textWidth = instance.measureText(text).width;
        return textWidth;
    }
}
_TextMeasure_key = new WeakMap(), _TextMeasure_map = new WeakMap(), _TextMeasure_instances = new WeakSet(), _TextMeasure_getInstance = function _TextMeasure_getInstance() {
    canvasCtx = canvasCtx || document.createElement("canvas").getContext("2d");
    return canvasCtx;
};
let instance = null;
const getTextMeasureInstance = () => {
    instance = instance || new TextMeasure();
    return instance;
};

const addContentBGName = "--diff-add-content--";
const delContentBGName = "--diff-del-content--";
const borderColorName = "--diff-border--";
const addLineNumberBGName = "--diff-add-lineNumber--";
const delLineNumberBGName = "--diff-del-lineNumber--";
const plainContentBGName = "--diff-plain-content--";
const expandContentBGName = "--diff-expand-content--";
const plainLineNumberColorName = "--diff-plain-lineNumber-color--";
const expandLineNumberColorName = "--diff-expand-lineNumber-color--";
const plainLineNumberBGName = "--diff-plain-lineNumber--";
const expandLineNumberBGName = "--diff-expand-lineNumber--";
const hunkContentBGName = "--diff-hunk-content--";
const hunkContentColorName = "--diff-hunk-content-color--";
const hunkLineNumberBGName = "--diff-hunk-lineNumber--";
const addWidgetBGName = "--diff-add-widget--";
const addWidgetColorName = "--diff-add-widget-color--";
const emptyBGName = "--diff-empty-content--";
const getContentBG = (isAdded, isDelete, hasDiff) => {
    return isAdded
        ? `var(${addContentBGName})`
        : isDelete
            ? `var(${delContentBGName})`
            : hasDiff
                ? `var(${plainContentBGName})`
                : `var(${expandContentBGName})`;
};
const getLineNumberBG = (isAdded, isDelete, hasDiff) => {
    return isAdded
        ? `var(${addLineNumberBGName})`
        : isDelete
            ? `var(${delLineNumberBGName})`
            : hasDiff
                ? `var(${plainLineNumberBGName})`
                : `var(${expandLineNumberBGName})`;
};

const removeAllSelection = () => {
    var _a;
    (_a = window.getSelection()) === null || _a === void 0 ? void 0 : _a.removeAllRanges();
};
const syncScroll = (left, right) => {
    const onScroll = function (event) {
        if (event === null || event.target === null)
            return;
        if (event.target === left) {
            right.scrollTop = left.scrollTop;
            right.scrollLeft = left.scrollLeft;
        }
        else {
            left.scrollTop = right.scrollTop;
            left.scrollLeft = right.scrollLeft;
        }
    };
    if (!left.onscroll) {
        left.onscroll = onScroll;
    }
    if (!right.onscroll) {
        right.onscroll = onScroll;
    }
    return () => {
        left.onscroll = null;
        right.onscroll = null;
    };
};
const getElementRoot = (element) => {
    if (element) {
        const root = element.getRootNode();
        if (root instanceof ShadowRoot) {
            return root;
        }
        return element.ownerDocument;
    }
    return document;
};
const getDiffIdFromElement = (element) => {
    var _a, _b;
    if (element) {
        if (typeof element.closest === "function") {
            const diffRoot = element.closest('[data-component="git-diff-view"]');
            const ele = (_a = diffRoot === null || diffRoot === void 0 ? void 0 : diffRoot.querySelector) === null || _a === void 0 ? void 0 : _a.call(diffRoot, ".diff-view-wrapper");
            return (_b = ele === null || ele === void 0 ? void 0 : ele.getAttribute) === null || _b === void 0 ? void 0 : _b.call(ele, "id");
        }
        else {
            let el = element;
            while (el) {
                if (el.getAttribute && el.getAttribute("data-component") === "git-diff-view") {
                    const ele = el.querySelector(".diff-view-wrapper");
                    return ele === null || ele === void 0 ? void 0 : ele.getAttribute("id");
                }
                el = el.parentElement;
            }
        }
    }
};

const diffFontSizeName = "--diff-font-size--";
const diffAsideWidthName = "--diff-aside-width--";

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
const memoFunc = (func) => {
    const cache = {};
    return ((key) => {
        if (cache[key]) {
            return cache[key];
        }
        const result = func(key);
        cache[key] = result;
        return result;
    });
};

var NewLineSymbol;
(function (NewLineSymbol) {
    NewLineSymbol[NewLineSymbol["CRLF"] = 1] = "CRLF";
    NewLineSymbol[NewLineSymbol["CR"] = 2] = "CR";
    NewLineSymbol[NewLineSymbol["LF"] = 3] = "LF";
    NewLineSymbol[NewLineSymbol["NEWLINE"] = 4] = "NEWLINE";
    NewLineSymbol[NewLineSymbol["NORMAL"] = 5] = "NORMAL";
    NewLineSymbol[NewLineSymbol["NULL"] = 6] = "NULL";
})(NewLineSymbol || (NewLineSymbol = {}));
var DiffModeEnum;
(function (DiffModeEnum) {
    // github like
    DiffModeEnum[DiffModeEnum["SplitGitHub"] = 1] = "SplitGitHub";
    // gitlab like
    DiffModeEnum[DiffModeEnum["SplitGitLab"] = 2] = "SplitGitLab";
    DiffModeEnum[DiffModeEnum["Split"] = 3] = "Split";
    DiffModeEnum[DiffModeEnum["Unified"] = 4] = "Unified";
})(DiffModeEnum || (DiffModeEnum = {}));

const useIsMounted = () => {
    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIsMounted(true);
    }, []);
    return isMounted;
};

const isClient = typeof window !== "undefined";
const useSafeLayout = isClient ? useLayoutEffect : useEffect;

const useTextWidth = ({ text, font, }) => {
    const [width, setWidth] = useState(() => {
        const fontSize = parseInt(font.fontSize || "13");
        let baseSize = 6;
        baseSize += fontSize > 10 ? (fontSize - 10) * 0.6 : 0;
        return baseSize * text.length;
    });
    useSafeLayout(() => {
        const width = getTextMeasureInstance().measure(text, font);
        setWidth(width);
    }, [text, font]);
    return width;
};

const DiffSplitAddWidget = ({ side, className, lineNumber, onWidgetClick, onOpenAddWidget, }) => {
    return (jsx("div", { "data-add-widget": SplitSide[side], className: "diff-add-widget-wrapper invisible select-none transition-transform hover:scale-110 group-hover:visible" +
            (className ? " " + className : ""), style: {
            width: `calc(var(${diffFontSizeName}) * 1.4)`,
            height: `calc(var(${diffFontSizeName}) * 1.4)`,
            top: `calc(var(${diffFontSizeName}) * 0.1)`,
        }, children: jsx("button", { className: "diff-add-widget z-[1] flex h-full w-full origin-center cursor-pointer items-center justify-center rounded-md text-[1.2em]", style: {
                color: `var(${addWidgetColorName})`,
                backgroundColor: `var(${addWidgetBGName})`,
            }, onMouseDown: (e) => {
                e.stopPropagation();
                onOpenAddWidget(lineNumber, side);
                onWidgetClick === null || onWidgetClick === void 0 ? void 0 : onWidgetClick(lineNumber, side);
            }, children: "+" }) }));
};
const DiffUnifiedAddWidget = ({ lineNumber, side, onWidgetClick, onOpenAddWidget, }) => {
    return (jsx("div", { "data-add-widget": SplitSide[side], className: "diff-add-widget-wrapper invisible absolute left-[100%] translate-x-[-50%] select-none transition-transform hover:scale-110 group-hover:visible", style: {
            width: `calc(var(${diffFontSizeName}) * 1.4)`,
            height: `calc(var(${diffFontSizeName}) * 1.4)`,
            top: `calc(var(${diffFontSizeName}) * 0.1)`,
        }, children: jsx("button", { className: "diff-add-widget z-[1] flex h-full w-full origin-center cursor-pointer items-center justify-center rounded-md text-[1.2em]", style: {
                color: `var(${addWidgetColorName})`,
                backgroundColor: `var(${addWidgetBGName})`,
            }, onMouseDown: (e) => {
                e.stopPropagation();
                onOpenAddWidget(lineNumber, side);
                onWidgetClick === null || onWidgetClick === void 0 ? void 0 : onWidgetClick(lineNumber, side);
            }, children: "+" }) }));
};

const DiffNoNewLine = () => {
    return (jsxs("svg", { "aria-label": "No newline at end of file", role: "img", viewBox: "0 0 16 16", version: "1.1", fill: "currentColor", children: [jsx("path", { d: "M4.25 7.25a.75.75 0 0 0 0 1.5h7.5a.75.75 0 0 0 0-1.5h-7.5Z" }), jsx("path", { d: "M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0Zm-1.5 0a6.5 6.5 0 1 0-13 0 6.5 6.5 0 0 0 13 0Z" })] }));
};

const temp = {};
const formatStringToCamelCase = (str) => {
    if (str.startsWith("--"))
        return str;
    const splitted = str.split("-");
    if (splitted.length === 1)
        return splitted[0];
    return (splitted[0] +
        splitted
            .slice(1)
            .map((word) => word[0].toUpperCase() + word.slice(1))
            .join(""));
};
const getStyleObjectFromString = memoFunc((str) => {
    if (!str)
        return temp;
    const style = {};
    str.split(";").forEach((el) => {
        const [property, value] = el.split(":");
        if (!property)
            return;
        const formattedProperty = formatStringToCamelCase(property.trim());
        style[formattedProperty] = value.trim();
    });
    return style;
});
const DiffString = ({ rawLine, diffLine, operator, plainLine, enableWrap, }) => {
    const changes = diffLine === null || diffLine === void 0 ? void 0 : diffLine.changes;
    if (changes === null || changes === void 0 ? void 0 : changes.hasLineChange) {
        const isNewLineSymbolChanged = changes.newLineSymbol;
        if (!(diffLine === null || diffLine === void 0 ? void 0 : diffLine.plainTemplate) && typeof getPlainDiffTemplate === "function") {
            getPlainDiffTemplate({ diffLine: diffLine, rawLine, operator: operator });
        }
        if (diffLine === null || diffLine === void 0 ? void 0 : diffLine.plainTemplate) {
            return (jsxs("span", { className: "diff-line-content-raw", children: [jsx("span", { "data-template": true, dangerouslySetInnerHTML: { __html: diffLine.plainTemplate } }), isNewLineSymbolChanged === NewLineSymbol.NEWLINE && (jsx("span", { "data-no-newline-at-end-of-file-symbol": true, className: enableWrap ? "block !text-red-500" : "inline-block align-middle !text-red-500", style: {
                            width: `var(${diffFontSizeName})`,
                            height: `var(${diffFontSizeName})`,
                        }, children: jsx(DiffNoNewLine, {}) }))] }));
        }
    }
    if (plainLine && !(plainLine === null || plainLine === void 0 ? void 0 : plainLine.template)) {
        // eslint-disable-next-line react-hooks/immutability
        plainLine.template = getPlainLineTemplate(plainLine.value);
    }
    if (plainLine === null || plainLine === void 0 ? void 0 : plainLine.template) {
        return (jsx("span", { className: "diff-line-content-raw", children: jsx("span", { "data-template": true, dangerouslySetInnerHTML: { __html: plainLine.template } }) }));
    }
    return jsx("span", { className: "diff-line-content-raw", children: rawLine });
};
const DiffSyntax = ({ rawLine, diffFile, diffLine, operator, syntaxLine, enableWrap, }) => {
    var _a;
    if (!syntaxLine) {
        return jsx(DiffString, { rawLine: rawLine, diffLine: diffLine, operator: operator, enableWrap: enableWrap });
    }
    const changes = diffLine === null || diffLine === void 0 ? void 0 : diffLine.changes;
    if (changes === null || changes === void 0 ? void 0 : changes.hasLineChange) {
        const isNewLineSymbolChanged = changes.newLineSymbol;
        if (!(diffLine === null || diffLine === void 0 ? void 0 : diffLine.syntaxTemplate) && typeof getSyntaxDiffTemplate === "function") {
            getSyntaxDiffTemplate({ diffFile, diffLine: diffLine, syntaxLine, operator: operator });
        }
        if (diffLine === null || diffLine === void 0 ? void 0 : diffLine.syntaxTemplate) {
            return (jsxs("span", { className: "diff-line-syntax-raw", children: [jsx("span", { "data-template": true, dangerouslySetInnerHTML: { __html: diffLine.syntaxTemplate } }), isNewLineSymbolChanged === NewLineSymbol.NEWLINE && (jsx("span", { "data-no-newline-at-end-of-file-symbol": true, className: enableWrap ? "block !text-red-500" : "inline-block align-middle !text-red-500", style: {
                            width: `var(${diffFontSizeName})`,
                            height: `var(${diffFontSizeName})`,
                        }, children: jsx(DiffNoNewLine, {}) }))] }));
        }
    }
    if (!syntaxLine.template) {
        // eslint-disable-next-line react-hooks/immutability
        syntaxLine.template = getSyntaxLineTemplate(syntaxLine);
    }
    if (syntaxLine === null || syntaxLine === void 0 ? void 0 : syntaxLine.template) {
        return (jsx("span", { className: "diff-line-syntax-raw", children: jsx("span", { "data-template": true, dangerouslySetInnerHTML: { __html: syntaxLine.template } }) }));
    }
    return (jsx("span", { className: "diff-line-syntax-raw", children: (_a = syntaxLine === null || syntaxLine === void 0 ? void 0 : syntaxLine.nodeList) === null || _a === void 0 ? void 0 : _a.map(({ node, wrapper }, index) => {
            var _a, _b, _c;
            return (jsx("span", { "data-start": node.startIndex, "data-end": node.endIndex, className: (_b = (_a = wrapper === null || wrapper === void 0 ? void 0 : wrapper.properties) === null || _a === void 0 ? void 0 : _a.className) === null || _b === void 0 ? void 0 : _b.join(" "), style: getStyleObjectFromString(((_c = wrapper === null || wrapper === void 0 ? void 0 : wrapper.properties) === null || _c === void 0 ? void 0 : _c.style) || ""), children: node.value }, index));
        }) }));
};
const DiffContent = ({ diffFile, diffLine, rawLine, plainLine, syntaxLine, enableWrap, enableHighlight, }) => {
    var _a;
    const isAdded = (diffLine === null || diffLine === void 0 ? void 0 : diffLine.type) === DiffLineType.Add;
    const isDelete = (diffLine === null || diffLine === void 0 ? void 0 : diffLine.type) === DiffLineType.Delete;
    const isMaxLineLengthToIgnoreSyntax = syntaxLine && ((_a = syntaxLine === null || syntaxLine === void 0 ? void 0 : syntaxLine.nodeList) === null || _a === void 0 ? void 0 : _a.length) > 150;
    return (jsxs("div", { className: "diff-line-content-item pl-[2.0em]", 
        // data-val={rawLine}
        style: {
            whiteSpace: enableWrap ? "pre-wrap" : "pre",
            wordBreak: enableWrap ? "break-all" : "initial",
        }, children: [jsx("span", { "data-operator": isAdded ? "+" : isDelete ? "-" : undefined, className: "diff-line-content-operator ml-[-1.5em] inline-block w-[1.5em] select-none indent-[0.2em]", children: isAdded ? "+" : isDelete ? "-" : " " }), enableHighlight && syntaxLine && !isMaxLineLengthToIgnoreSyntax ? (jsx(DiffSyntax, { operator: isAdded ? "add" : isDelete ? "del" : undefined, rawLine: rawLine, diffFile: diffFile, diffLine: diffLine, syntaxLine: syntaxLine, enableWrap: enableWrap })) : (jsx(DiffString, { operator: isAdded ? "add" : isDelete ? "del" : undefined, rawLine: rawLine, diffLine: diffLine, plainLine: plainLine, enableWrap: enableWrap }))] }));
};

const DiffViewContext = createContext(null);
DiffViewContext.displayName = "DiffViewContext";
const useDiffViewContext = () => useContext(DiffViewContext);

const DiffWidgetContext = createContext(null);
DiffWidgetContext.displayName = "DiffWidgetContext";
const useDiffWidgetContext = () => useContext(DiffWidgetContext);

const InternalDiffSplitLine$1 = ({ index, diffFile, lineNumber, side, enableAddWidget, enableHighlight, }) => {
    var _a, _b, _c, _d, _e;
    const getCurrentSyntaxLine = side === SplitSide.old ? diffFile.getOldSyntaxLine : diffFile.getNewSyntaxLine;
    const getCurrentPlainLine = side === SplitSide.old ? diffFile.getOldPlainLine : diffFile.getNewPlainLine;
    const oldLine = diffFile.getSplitLeftLine(index);
    const newLine = diffFile.getSplitRightLine(index);
    const currentLine = side === SplitSide.old ? oldLine : newLine;
    const hasDiff = !!(currentLine === null || currentLine === void 0 ? void 0 : currentLine.diff);
    const hasContent = !!(currentLine === null || currentLine === void 0 ? void 0 : currentLine.lineNumber);
    const hasChange = checkDiffLineIncludeChange(currentLine === null || currentLine === void 0 ? void 0 : currentLine.diff);
    const isAdded = ((_a = currentLine === null || currentLine === void 0 ? void 0 : currentLine.diff) === null || _a === void 0 ? void 0 : _a.type) === DiffLineType.Add;
    const isDelete = ((_b = currentLine === null || currentLine === void 0 ? void 0 : currentLine.diff) === null || _b === void 0 ? void 0 : _b.type) === DiffLineType.Delete;
    const { useDiffContext } = useDiffViewContext();
    const onAddWidgetClick = useDiffContext.getReadonlyState().onAddWidgetClick;
    const { useWidget } = useDiffWidgetContext();
    const setWidget = useWidget.getReadonlyState().setWidget;
    const contentBG = getContentBG(isAdded, isDelete, hasDiff);
    const lineNumberBG = getLineNumberBG(isAdded, isDelete, hasDiff);
    const syntaxLine = getCurrentSyntaxLine((_c = currentLine.lineNumber) !== null && _c !== void 0 ? _c : -1);
    const plainLine = getCurrentPlainLine((_d = currentLine.lineNumber) !== null && _d !== void 0 ? _d : -1);
    return (jsx("tr", { "data-line": lineNumber, "data-state": hasDiff || !hasContent ? "diff" : "plain", "data-side": SplitSide[side], className: "diff-line" + (hasContent ? " group" : ""), children: hasContent ? (jsxs(Fragment, { children: [jsxs("td", { className: `diff-line-${SplitSide[side]}-num sticky left-0 z-[1] w-[1%] min-w-[40px] select-none pl-[10px] pr-[10px] text-right align-top`, style: {
                        backgroundColor: lineNumberBG,
                        color: `var(${hasDiff ? plainLineNumberColorName : expandLineNumberColorName})`,
                        width: `var(${diffAsideWidthName})`,
                        minWidth: `var(${diffAsideWidthName})`,
                        maxWidth: `var(${diffAsideWidthName})`,
                    }, children: [hasDiff && enableAddWidget && (jsx(DiffSplitAddWidget, { index: index, lineNumber: (_e = currentLine.lineNumber) !== null && _e !== void 0 ? _e : -1, side: side, diffFile: diffFile, onWidgetClick: (...props) => { var _a; return (_a = onAddWidgetClick.current) === null || _a === void 0 ? void 0 : _a.call(onAddWidgetClick, ...props); }, className: "absolute left-[100%] z-[1] translate-x-[-50%]", onOpenAddWidget: (lineNumber, side) => setWidget({ lineNumber: lineNumber, side: side }) })), jsx("span", { "data-line-num": currentLine.lineNumber, style: { opacity: hasChange ? undefined : 0.5 }, children: currentLine.lineNumber })] }), jsx("td", { className: `diff-line-${SplitSide[side]}-content pr-[10px] align-top`, style: { backgroundColor: contentBG }, children: jsx(DiffContent, { enableWrap: false, diffFile: diffFile, rawLine: (currentLine === null || currentLine === void 0 ? void 0 : currentLine.value) || "", diffLine: currentLine === null || currentLine === void 0 ? void 0 : currentLine.diff, plainLine: plainLine, syntaxLine: syntaxLine, enableHighlight: enableHighlight }) })] })) : (jsx("td", { className: `diff-line-${SplitSide[side]}-placeholder select-none`, style: { backgroundColor: `var(${emptyBGName})` }, colSpan: 2, children: jsx("span", { children: "\u2002" }) })) }));
};
const InternalDiffSplitContentLine$1 = ({ index, diffFile, lineNumber, side, enableAddWidget, enableHighlight, }) => {
    const getCurrentLine = side === SplitSide.old ? diffFile.getSplitLeftLine : diffFile.getSplitRightLine;
    const currentLine = getCurrentLine(index);
    if (currentLine === null || currentLine === void 0 ? void 0 : currentLine.isHidden)
        return null;
    return (jsx(InternalDiffSplitLine$1, { index: index, diffFile: diffFile, lineNumber: lineNumber, side: side, enableAddWidget: enableAddWidget, enableHighlight: enableHighlight }));
};
const DiffSplitContentLine$1 = memo(InternalDiffSplitContentLine$1);

const useDomWidth = ({ selector, enable }) => {
    const [width, setWidth] = useState(0);
    const { useDiffContext } = useDiffViewContext();
    const { id, mounted, dom } = useDiffContext.useShallowStableSelector((s) => ({
        id: s.id,
        mounted: s.mounted,
        dom: s.dom,
    }));
    useEffect(() => {
        if (enable) {
            const rootDocument = getElementRoot(dom);
            const container = rootDocument.querySelector(`#diff-root${id}`);
            const wrapper = container === null || container === void 0 ? void 0 : container.querySelector(selector);
            if (!wrapper)
                return;
            const typedWrapper = wrapper;
            const cb = () => {
                var _a;
                const rect = wrapper === null || wrapper === void 0 ? void 0 : wrapper.getBoundingClientRect();
                setWidth((_a = rect === null || rect === void 0 ? void 0 : rect.width) !== null && _a !== void 0 ? _a : 0);
            };
            cb();
            const cleanCb = () => {
                var _a, _b, _c;
                (_a = typedWrapper === null || typedWrapper === void 0 ? void 0 : typedWrapper.__observeCallback) === null || _a === void 0 ? void 0 : _a.delete(cb);
                if (((_b = typedWrapper === null || typedWrapper === void 0 ? void 0 : typedWrapper.__observeCallback) === null || _b === void 0 ? void 0 : _b.size) === 0) {
                    (_c = typedWrapper.__observeInstance) === null || _c === void 0 ? void 0 : _c.disconnect();
                    typedWrapper.removeAttribute("data-observe");
                    delete typedWrapper.__observeCallback;
                    delete typedWrapper.__observeInstance;
                }
            };
            if (typedWrapper.__observeCallback) {
                typedWrapper.__observeCallback.add(cb);
                return () => cleanCb();
            }
            typedWrapper.__observeCallback = new Set();
            typedWrapper.__observeCallback.add(cb);
            const observer = new ResizeObserver(() => { var _a; return (_a = typedWrapper === null || typedWrapper === void 0 ? void 0 : typedWrapper.__observeCallback) === null || _a === void 0 ? void 0 : _a.forEach((cb) => cb()); });
            typedWrapper.__observeInstance = observer;
            observer.observe(typedWrapper);
            typedWrapper.setAttribute("data-observe", "height");
            return () => cleanCb();
        }
    }, [selector, enable, id, mounted, dom]);
    return width;
};

const useSyncHeight = ({ selector, wrapper, side, enable, }) => {
    const { useDiffContext } = useDiffViewContext();
    const { id, mounted, dom } = useDiffContext.useShallowStableSelector((s) => ({
        id: s.id,
        mounted: s.mounted,
        dom: s.dom,
    }));
    useEffect(() => {
        if (enable) {
            const rootDocument = getElementRoot(dom);
            const container = rootDocument.querySelector(`#diff-root${id}`);
            const elements = Array.from((container === null || container === void 0 ? void 0 : container.querySelectorAll(selector)) || []);
            const wrappers = wrapper ? Array.from((container === null || container === void 0 ? void 0 : container.querySelectorAll(wrapper)) || []) : elements;
            if (elements.length === 2 && wrappers.length === 2) {
                const ele1 = elements[0];
                const ele2 = elements[1];
                const wrapper1 = wrappers[0];
                const wrapper2 = wrappers[1];
                const target = ele1.getAttribute("data-side") === side ? ele1 : ele2;
                const typedTarget = target;
                const cb = () => {
                    ele1.style.height = "auto";
                    ele2.style.height = "auto";
                    const rect1 = ele1.getBoundingClientRect();
                    const rect2 = ele2.getBoundingClientRect();
                    const maxHeight = Math.max(rect1.height, rect2.height);
                    wrapper1.style.height = maxHeight + "px";
                    wrapper2.style.height = maxHeight + "px";
                    wrapper1.setAttribute("data-sync-height", String(maxHeight));
                    wrapper2.setAttribute("data-sync-height", String(maxHeight));
                };
                cb();
                const cleanCb = () => {
                    var _a, _b, _c;
                    (_a = typedTarget === null || typedTarget === void 0 ? void 0 : typedTarget.__observeCallback) === null || _a === void 0 ? void 0 : _a.delete(cb);
                    if (((_b = typedTarget === null || typedTarget === void 0 ? void 0 : typedTarget.__observeCallback) === null || _b === void 0 ? void 0 : _b.size) === 0) {
                        (_c = typedTarget.__observeInstance) === null || _c === void 0 ? void 0 : _c.disconnect();
                        typedTarget.removeAttribute("data-observe");
                        delete typedTarget.__observeCallback;
                        delete typedTarget.__observeInstance;
                    }
                };
                if (typedTarget.__observeCallback) {
                    typedTarget.__observeCallback.add(cb);
                    return () => cleanCb();
                }
                typedTarget.__observeCallback = new Set();
                typedTarget.__observeCallback.add(cb);
                const observer = new ResizeObserver(() => { var _a; return (_a = typedTarget === null || typedTarget === void 0 ? void 0 : typedTarget.__observeCallback) === null || _a === void 0 ? void 0 : _a.forEach((cb) => cb()); });
                typedTarget.__observeInstance = observer;
                observer.observe(target);
                target.setAttribute("data-observe", "height");
                return () => cleanCb();
            }
        }
    }, [selector, enable, side, id, wrapper, mounted, dom]);
};

const InternalDiffSplitExtendLine$1 = ({ index, diffFile, oldLineExtend, newLineExtend, side, lineNumber, }) => {
    const { useDiffContext } = useDiffViewContext();
    const oldLine = diffFile.getSplitLeftLine(index);
    const newLine = diffFile.getSplitRightLine(index);
    const renderExtendLine = useDiffContext.useShallowStableSelector((s) => s.renderExtendLine);
    const currentExtend = side === SplitSide.old ? oldLineExtend : newLineExtend;
    const otherSide = side === SplitSide.old ? SplitSide.new : SplitSide.old;
    const currentLineNumber = side === SplitSide.old ? oldLine.lineNumber : newLine.lineNumber;
    const currentSideHasExtend = (currentExtend === null || currentExtend === void 0 ? void 0 : currentExtend.data) !== undefined && (currentExtend === null || currentExtend === void 0 ? void 0 : currentExtend.data) !== null;
    const hasExtend = ((oldLineExtend === null || oldLineExtend === void 0 ? void 0 : oldLineExtend.data) !== undefined && (oldLineExtend === null || oldLineExtend === void 0 ? void 0 : oldLineExtend.data) !== null) ||
        ((newLineExtend === null || newLineExtend === void 0 ? void 0 : newLineExtend.data) !== undefined && (newLineExtend === null || newLineExtend === void 0 ? void 0 : newLineExtend.data) !== null);
    const currentExtendRendered = hasExtend &&
        (renderExtendLine === null || renderExtendLine === void 0 ? void 0 : renderExtendLine({
            diffFile,
            side,
            lineNumber: currentLineNumber !== null && currentLineNumber !== void 0 ? currentLineNumber : -1,
            data: currentExtend === null || currentExtend === void 0 ? void 0 : currentExtend.data,
            onUpdate: diffFile.notifyAll,
        }));
    useSyncHeight({
        selector: `div[data-line="${lineNumber}-extend-content"]`,
        wrapper: `tr[data-line="${lineNumber}-extend"]`,
        side: SplitSide[currentSideHasExtend ? side : otherSide],
        enable: hasExtend && typeof renderExtendLine === "function",
    });
    const width = useDomWidth({
        selector: side === SplitSide.old ? ".old-diff-table-wrapper" : ".new-diff-table-wrapper",
        enable: currentSideHasExtend && typeof renderExtendLine === "function",
    });
    if (!renderExtendLine)
        return null;
    return (jsx("tr", { "data-line": `${lineNumber}-extend`, "data-state": "extend", "data-side": SplitSide[side], className: "diff-line diff-line-extend", children: currentSideHasExtend ? (jsx("td", { className: `diff-line-extend-${SplitSide[side]}-content p-0`, colSpan: 2, children: jsx("div", { "data-line": `${lineNumber}-extend-content`, "data-side": SplitSide[side], className: "diff-line-extend-wrapper sticky left-0 z-[1]", style: { width }, children: width > 0 && currentExtendRendered }) })) : (jsx("td", { className: `diff-line-extend-${SplitSide[side]}-placeholder select-none p-0`, style: { backgroundColor: `var(${emptyBGName})` }, colSpan: 2, children: jsx("div", { "data-line": `${lineNumber}-extend-content`, "data-side": SplitSide[side] }) })) }));
};
const DiffSplitExtendLine$1 = ({ index, diffFile, side, lineNumber, }) => {
    const { useDiffContext } = useDiffViewContext();
    const oldLine = diffFile.getSplitLeftLine(index);
    const newLine = diffFile.getSplitRightLine(index);
    const { oldLineExtend, newLineExtend } = useDiffContext(React.useCallback((s) => {
        var _a, _b, _c, _d, _e, _f;
        return ({
            oldLineExtend: (_b = (_a = s.extendData) === null || _a === void 0 ? void 0 : _a.oldFile) === null || _b === void 0 ? void 0 : _b[(_c = oldLine === null || oldLine === void 0 ? void 0 : oldLine.lineNumber) !== null && _c !== void 0 ? _c : -1],
            newLineExtend: (_e = (_d = s.extendData) === null || _d === void 0 ? void 0 : _d.newFile) === null || _e === void 0 ? void 0 : _e[(_f = newLine === null || newLine === void 0 ? void 0 : newLine.lineNumber) !== null && _f !== void 0 ? _f : -1],
        });
    }, [oldLine === null || oldLine === void 0 ? void 0 : oldLine.lineNumber, newLine === null || newLine === void 0 ? void 0 : newLine.lineNumber]));
    const hasExtend = (oldLineExtend === null || oldLineExtend === void 0 ? void 0 : oldLineExtend.data) || (newLineExtend === null || newLineExtend === void 0 ? void 0 : newLineExtend.data);
    // if the expand action not enabled, the `isHidden` property will never change
    const enableExpand = diffFile.getExpandEnabled();
    const currentLine = side === SplitSide.old ? oldLine : newLine;
    const currentIsShow = hasExtend && (!currentLine.isHidden || !enableExpand);
    if (!currentIsShow)
        return null;
    return (jsx(InternalDiffSplitExtendLine$1, { side: side, index: index, diffFile: diffFile, lineNumber: lineNumber, oldLineExtend: oldLineExtend, newLineExtend: newLineExtend }));
};

const ExpandDown = ({ className }) => {
    return (jsx("svg", { "aria-hidden": "true", height: "16", viewBox: "0 0 16 16", version: "1.1", width: "16", className: className, children: jsx("path", { d: "m8.177 14.323 2.896-2.896a.25.25 0 0 0-.177-.427H8.75V7.764a.75.75 0 1 0-1.5 0V11H5.104a.25.25 0 0 0-.177.427l2.896 2.896a.25.25 0 0 0 .354 0ZM2.25 5a.75.75 0 0 0 0-1.5h-.5a.75.75 0 0 0 0 1.5h.5ZM6 4.25a.75.75 0 0 1-.75.75h-.5a.75.75 0 0 1 0-1.5h.5a.75.75 0 0 1 .75.75ZM8.25 5a.75.75 0 0 0 0-1.5h-.5a.75.75 0 0 0 0 1.5h.5ZM12 4.25a.75.75 0 0 1-.75.75h-.5a.75.75 0 0 1 0-1.5h.5a.75.75 0 0 1 .75.75Zm2.25.75a.75.75 0 0 0 0-1.5h-.5a.75.75 0 0 0 0 1.5h.5Z" }) }));
};
const ExpandUp = ({ className }) => {
    return (jsx("svg", { "aria-hidden": "true", height: "16", viewBox: "0 0 16 16", version: "1.1", width: "16", className: className, children: jsx("path", { d: "M7.823 1.677 4.927 4.573A.25.25 0 0 0 5.104 5H7.25v3.236a.75.75 0 1 0 1.5 0V5h2.146a.25.25 0 0 0 .177-.427L8.177 1.677a.25.25 0 0 0-.354 0ZM13.75 11a.75.75 0 0 0 0 1.5h.5a.75.75 0 0 0 0-1.5h-.5Zm-3.75.75a.75.75 0 0 1 .75-.75h.5a.75.75 0 0 1 0 1.5h-.5a.75.75 0 0 1-.75-.75ZM7.75 11a.75.75 0 0 0 0 1.5h.5a.75.75 0 0 0 0-1.5h-.5ZM4 11.75a.75.75 0 0 1 .75-.75h.5a.75.75 0 0 1 0 1.5h-.5a.75.75 0 0 1-.75-.75ZM1.75 11a.75.75 0 0 0 0 1.5h.5a.75.75 0 0 0 0-1.5h-.5Z" }) }));
};
const ExpandAll = ({ className }) => {
    return (jsx("svg", { "aria-hidden": "true", height: "16", viewBox: "0 0 16 16", version: "1.1", width: "16", className: className, children: jsx("path", { d: "m8.177.677 2.896 2.896a.25.25 0 0 1-.177.427H8.75v1.25a.75.75 0 0 1-1.5 0V4H5.104a.25.25 0 0 1-.177-.427L7.823.677a.25.25 0 0 1 .354 0ZM7.25 10.75a.75.75 0 0 1 1.5 0V12h2.146a.25.25 0 0 1 .177.427l-2.896 2.896a.25.25 0 0 1-.354 0l-2.896-2.896A.25.25 0 0 1 5.104 12H7.25v-1.25Zm-5-2a.75.75 0 0 0 0-1.5h-.5a.75.75 0 0 0 0 1.5h.5ZM6 8a.75.75 0 0 1-.75.75h-.5a.75.75 0 0 1 0-1.5h.5A.75.75 0 0 1 6 8Zm2.25.75a.75.75 0 0 0 0-1.5h-.5a.75.75 0 0 0 0 1.5h.5ZM12 8a.75.75 0 0 1-.75.75h-.5a.75.75 0 0 1 0-1.5h.5A.75.75 0 0 1 12 8Zm2.25.75a.75.75 0 0 0 0-1.5h-.5a.75.75 0 0 0 0 1.5h.5Z" }) }));
};

const InternalDiffSplitHunkLineGitHub = ({ index, diffFile, side, lineNumber, }) => {
    var _a;
    const currentHunk = diffFile.getSplitHunkLine(index);
    const expandEnabled = diffFile.getExpandEnabled();
    useSyncHeight({
        selector: `tr[data-line="${lineNumber}-hunk"]`,
        side: SplitSide[SplitSide.old],
        enable: side === SplitSide.new,
    });
    const enableHunkAction = side === SplitSide.old;
    const couldExpand = expandEnabled && currentHunk && currentHunk.splitInfo;
    const isExpandAll = currentHunk &&
        currentHunk.splitInfo &&
        currentHunk.splitInfo.endHiddenIndex - currentHunk.splitInfo.startHiddenIndex < composeLen;
    const isFirstLine = currentHunk && currentHunk.isFirst;
    const isLastLine = currentHunk && currentHunk.isLast;
    return (jsx("tr", { "data-line": `${lineNumber}-hunk`, "data-state": "hunk", "data-side": SplitSide[side], className: "diff-line diff-line-hunk", children: enableHunkAction ? (jsxs(Fragment, { children: [jsx("td", { className: "diff-line-hunk-action sticky left-0 w-[1%] min-w-[40px] select-none p-[1px]", style: {
                        backgroundColor: `var(${hunkLineNumberBGName})`,
                        color: `var(${plainLineNumberColorName})`,
                        width: `var(${diffAsideWidthName})`,
                        minWidth: `var(${diffAsideWidthName})`,
                        maxWidth: `var(${diffAsideWidthName})`,
                    }, children: couldExpand ? (isFirstLine ? (jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand Up", "data-title": "Expand Up", onClick: () => diffFile.onSplitHunkExpand("up", index), children: jsx(ExpandUp, { className: "fill-current" }) })) : isLastLine ? (jsx("button", { className: "diff-widget-tooltip relative flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand Down", "data-title": "Expand Down", onClick: () => diffFile.onSplitHunkExpand("down", index), children: jsx(ExpandDown, { className: "fill-current" }) })) : isExpandAll ? (jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand All", "data-title": "Expand All", onClick: () => diffFile.onSplitHunkExpand("all", index), children: jsx(ExpandAll, { className: "fill-current" }) })) : (jsxs(Fragment, { children: [jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[2px]", title: "Expand Down", "data-title": "Expand Down", onClick: () => diffFile.onSplitHunkExpand("down", index), children: jsx(ExpandDown, { className: "fill-current" }) }), jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[2px]", title: "Expand Up", "data-title": "Expand Up", onClick: () => diffFile.onSplitHunkExpand("up", index), children: jsx(ExpandUp, { className: "fill-current" }) })] }))) : (jsx("div", { className: "min-h-[28px]", children: "\u2002" })) }), jsx("td", { className: "diff-line-hunk-content pr-[10px] align-middle", style: { backgroundColor: `var(${hunkContentBGName})` }, children: jsx("div", { className: "pl-[1.5em]", style: {
                            color: `var(${hunkContentColorName})`,
                        }, children: ((_a = currentHunk.splitInfo) === null || _a === void 0 ? void 0 : _a.plainText) || currentHunk.text }) })] })) : (jsx("td", { className: "diff-line-hunk-placeholder select-none", colSpan: 2, style: { backgroundColor: `var(${hunkContentBGName})` }, children: jsx("div", { className: "min-h-[28px]", children: "\u2002" }) })) }));
};
const InternalDiffSplitHunkLineGitLab = ({ index, diffFile, side, lineNumber, }) => {
    var _a;
    const currentHunk = diffFile.getSplitHunkLine(index);
    const expandEnabled = diffFile.getExpandEnabled();
    useSyncHeight({
        selector: `tr[data-line="${lineNumber}-hunk"]`,
        side: SplitSide[SplitSide.old],
        enable: side === SplitSide.new,
    });
    const couldExpand = expandEnabled && currentHunk && currentHunk.splitInfo;
    const isExpandAll = currentHunk &&
        currentHunk.splitInfo &&
        currentHunk.splitInfo.endHiddenIndex - currentHunk.splitInfo.startHiddenIndex < composeLen;
    const isFirstLine = currentHunk && currentHunk.isFirst;
    const isLastLine = currentHunk && currentHunk.isLast;
    return (jsxs("tr", { "data-line": `${lineNumber}-hunk`, "data-state": "hunk", "data-side": SplitSide[side], className: "diff-line diff-line-hunk", children: [jsx("td", { className: "diff-line-hunk-action sticky left-0 w-[1%] min-w-[40px] select-none p-[1px]", style: {
                    backgroundColor: `var(${hunkLineNumberBGName})`,
                    color: `var(${plainLineNumberColorName})`,
                    width: `var(${diffAsideWidthName})`,
                    minWidth: `var(${diffAsideWidthName})`,
                    maxWidth: `var(${diffAsideWidthName})`,
                }, children: couldExpand ? (isFirstLine ? (jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand Up", "data-title": "Expand Up", onClick: () => diffFile.onSplitHunkExpand("up", index), children: jsx(ExpandUp, { className: "fill-current" }) })) : isLastLine ? (jsx("button", { className: "diff-widget-tooltip relative flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand Down", "data-title": "Expand Down", onClick: () => diffFile.onSplitHunkExpand("down", index), children: jsx(ExpandDown, { className: "fill-current" }) })) : isExpandAll ? (jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand All", "data-title": "Expand All", onClick: () => diffFile.onSplitHunkExpand("all", index), children: jsx(ExpandAll, { className: "fill-current" }) })) : (jsxs(Fragment, { children: [jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[2px]", title: "Expand Down", "data-title": "Expand Down", onClick: () => diffFile.onSplitHunkExpand("down", index), children: jsx(ExpandDown, { className: "fill-current" }) }), jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[2px]", title: "Expand Up", "data-title": "Expand Up", onClick: () => diffFile.onSplitHunkExpand("up", index), children: jsx(ExpandUp, { className: "fill-current" }) })] }))) : (jsx("div", { className: "min-h-[28px]", children: "\u2002" })) }), jsx("td", { className: "diff-line-hunk-content pr-[10px] align-middle", style: { backgroundColor: `var(${hunkContentBGName})` }, children: jsx("div", { className: "pl-[1.5em]", style: {
                        color: `var(${hunkContentColorName})`,
                    }, children: ((_a = currentHunk.splitInfo) === null || _a === void 0 ? void 0 : _a.plainText) || currentHunk.text }) })] }));
};
const InternalDiffSplitHunkLine$1 = ({ index, diffFile, side, lineNumber, }) => {
    const { useDiffContext } = useDiffViewContext();
    const diffViewMode = useDiffContext.useShallowStableSelector((s) => s.mode);
    if (diffViewMode === DiffModeEnum.SplitGitHub ||
        diffViewMode === DiffModeEnum.Split ||
        diffViewMode === DiffModeEnum.Unified) {
        return jsx(InternalDiffSplitHunkLineGitHub, { index: index, diffFile: diffFile, side: side, lineNumber: lineNumber });
    }
    else {
        return jsx(InternalDiffSplitHunkLineGitLab, { index: index, diffFile: diffFile, side: side, lineNumber: lineNumber });
    }
};
const DiffSplitHunkLine$1 = ({ index, diffFile, side, lineNumber, }) => {
    const currentHunk = diffFile.getSplitHunkLine(index);
    const currentIsShow = currentHunk &&
        currentHunk.splitInfo &&
        currentHunk.splitInfo.startHiddenIndex < currentHunk.splitInfo.endHiddenIndex;
    const currentIsPureHunk = currentHunk && diffFile._getIsPureDiffRender() && !currentHunk.splitInfo;
    if (!currentIsShow && !currentIsPureHunk)
        return null;
    return jsx(InternalDiffSplitHunkLine$1, { index: index, diffFile: diffFile, side: side, lineNumber: lineNumber });
};

const InternalDiffSplitWidgetLine$1 = ({ index, side, diffFile, lineNumber, }) => {
    var _a;
    const { useWidget } = useDiffWidgetContext();
    const { useDiffContext } = useDiffViewContext();
    const oldLine = diffFile.getSplitLeftLine(index);
    const newLine = diffFile.getSplitRightLine(index);
    const widgetSide = useWidget.useShallowStableSelector((s) => s.widgetSide);
    const widgetLineNumber = useWidget.getReadonlyState().widgetLineNumber;
    const setWidget = useWidget.getReadonlyState().setWidget;
    const oldLineWidget = oldLine.lineNumber && widgetSide === SplitSide.old && widgetLineNumber === oldLine.lineNumber;
    const newLineWidget = newLine.lineNumber && widgetSide === SplitSide.new && widgetLineNumber === newLine.lineNumber;
    const currentLine = side === SplitSide.old ? oldLine : newLine;
    const otherSide = side === SplitSide.old ? SplitSide.new : SplitSide.old;
    const currentHasWidget = side === SplitSide.old ? oldLineWidget : newLineWidget;
    const hasWidget = oldLineWidget || newLineWidget;
    const renderWidgetLine = useDiffContext.useShallowStableSelector((s) => s.renderWidgetLine);
    const currentWidgetRendered = currentHasWidget &&
        (renderWidgetLine === null || renderWidgetLine === void 0 ? void 0 : renderWidgetLine({
            diffFile,
            side,
            lineNumber: (_a = currentLine.lineNumber) !== null && _a !== void 0 ? _a : -1,
            onClose: () => setWidget({}),
        }));
    useSyncHeight({
        selector: `div[data-line="${lineNumber}-widget-content"]`,
        wrapper: `tr[data-line="${lineNumber}-widget"]`,
        side: SplitSide[currentHasWidget ? side : otherSide],
        enable: !!hasWidget && typeof renderWidgetLine === "function",
    });
    const width = useDomWidth({
        selector: side === SplitSide.old ? ".old-diff-table-wrapper" : ".new-diff-table-wrapper",
        enable: !!currentHasWidget && typeof renderWidgetLine === "function",
    });
    if (!renderWidgetLine)
        return null;
    return (jsx("tr", { "data-line": `${lineNumber}-widget`, "data-state": "widget", "data-side": SplitSide[side], className: "diff-line diff-line-widget", children: currentHasWidget ? (jsx("td", { className: `diff-line-widget-${SplitSide[side]}-content p-0`, colSpan: 2, children: jsx("div", { "data-line": `${lineNumber}-widget-content`, "data-side": SplitSide[side], className: "diff-line-widget-wrapper sticky left-0 z-[1]", style: { width }, children: width > 0 && currentWidgetRendered }) })) : (jsx("td", { className: `diff-line-widget-${SplitSide[side]}-placeholder p-0`, style: { backgroundColor: `var(${emptyBGName})` }, colSpan: 2, children: jsx("div", { "data-line": `${lineNumber}-widget-content`, "data-side": SplitSide[side] }) })) }));
};
// TODO! improve performance
const DiffSplitWidgetLine$1 = ({ index, side, diffFile, lineNumber, }) => {
    const { useWidget } = useDiffWidgetContext();
    const currentIsShow = useWidget.useShallowSelector(React.useCallback((s) => {
        const widgetLineNumber = s.widgetLineNumber;
        const widgetSide = s.widgetSide;
        const oldLine = diffFile.getSplitLeftLine(index);
        const newLine = diffFile.getSplitRightLine(index);
        const oldLineWidget = oldLine.lineNumber && widgetSide === SplitSide.old && widgetLineNumber === oldLine.lineNumber;
        const newLineWidget = newLine.lineNumber && widgetSide === SplitSide.new && widgetLineNumber === newLine.lineNumber;
        const currentIsShow = oldLineWidget || newLineWidget;
        return currentIsShow;
    }, [diffFile, index]), (p, c) => p === c);
    if (!currentIsShow)
        return null;
    return jsx(InternalDiffSplitWidgetLine$1, { index: index, side: side, diffFile: diffFile, lineNumber: lineNumber });
};

const DiffSplitViewTable = ({ side, diffFile, enableAddWidget, enableHighlight, onMouseDown, }) => {
    const className = side === SplitSide.new ? "new-diff-table" : "old-diff-table";
    const lines = getSplitContentLines(diffFile);
    // changes only on content / template rebuilds, not on expand / collapse
    const contentVersion = diffFile.getContentVersion();
    return (jsxs("table", { className: className + " w-full border-collapse border-spacing-0", "data-mode": SplitSide[side], children: [jsxs("colgroup", { children: [jsx("col", { className: `diff-table-${SplitSide[side]}-num-col` }), jsx("col", { className: `diff-table-${SplitSide[side]}-content-col` })] }), jsx("thead", { className: "hidden", children: jsxs("tr", { children: [jsxs("th", { scope: "col", children: [SplitSide[side], " line number"] }), jsxs("th", { scope: "col", children: [SplitSide[side], " line content"] })] }) }), jsxs("tbody", { className: "diff-table-body leading-[1.6]", onMouseDownCapture: onMouseDown, children: [lines.map((line) => (jsxs(Fragment$1, { children: [jsx(DiffSplitHunkLine$1, { index: line.index, side: side, lineNumber: line.lineNumber, diffFile: diffFile }), jsx(DiffSplitContentLine$1, { index: line.index, side: side, lineNumber: line.lineNumber, diffFile: diffFile, enableAddWidget: enableAddWidget, enableHighlight: enableHighlight, contentVersion: contentVersion }), jsx(DiffSplitWidgetLine$1, { index: line.index, side: side, lineNumber: line.lineNumber, diffFile: diffFile }), jsx(DiffSplitExtendLine$1, { index: line.index, side: side, lineNumber: line.lineNumber, diffFile: diffFile })] }, line.index))), jsx(DiffSplitHunkLine$1, { side: side, index: diffFile.splitLineLength, lineNumber: diffFile.splitLineLength, diffFile: diffFile })] })] }));
};
const DiffSplitViewNormal = memo(({ diffFile }) => {
    const ref1 = useRef(null);
    const ref2 = useRef(null);
    const ref = useRef(null);
    const tempRef = useRef(undefined);
    const splitLineLength = Math.max(diffFile.splitLineLength, diffFile.fileLineLength);
    const { useDiffContext } = useDiffViewContext();
    const { fontSize, enableAddWidget, enableHighlight } = useDiffContext.useShallowStableSelector((s) => ({
        fontSize: s.fontSize,
        enableAddWidget: s.enableAddWidget,
        enableHighlight: s.enableHighlight,
    }));
    useSyncExternalStore(diffFile.subscribe, diffFile.getUpdateCount, diffFile.getUpdateCount);
    useEffect(() => {
        const left = ref1.current;
        const right = ref2.current;
        if (!left || !right)
            return;
        return syncScroll(left, right);
    }, []);
    const font = React.useMemo(() => ({ fontSize: fontSize + "px", fontFamily: "Menlo, Consolas, monospace" }), [fontSize]);
    const _width = useTextWidth({
        text: splitLineLength.toString(),
        font,
    });
    const width = Math.max(40, _width + 20);
    const setStyle = (side) => {
        if (!ref.current)
            return;
        if (!side) {
            ref.current.textContent = "";
        }
        else {
            const id = `diff-root${diffFile.getId()}`;
            ref.current.textContent = `#${id} [data-state="extend"] {user-select: none} \n#${id} [data-state="hunk"] {user-select: none} \n#${id} [data-state="widget"] {user-select: none}`;
        }
    };
    const onMouseDown = (e) => {
        let ele = e.target;
        // need remove all the selection
        if (ele && ele instanceof HTMLElement && ele.nodeName === "BUTTON") {
            removeAllSelection();
            return;
        }
        const id = getDiffIdFromElement(ele);
        if (id && id !== `diff-root${diffFile.getId()}`) {
            return;
        }
        while (ele && ele instanceof HTMLElement) {
            const state = ele.getAttribute("data-state");
            const side = ele.getAttribute("data-side");
            if (side) {
                if (tempRef.current !== SplitSide[side]) {
                    tempRef.current = SplitSide[side];
                    setStyle(SplitSide[side]);
                    removeAllSelection();
                }
            }
            if (state) {
                if (state === "extend" || state === "hunk" || state === "widget") {
                    if (tempRef.current !== undefined) {
                        tempRef.current = undefined;
                        setStyle(undefined);
                        removeAllSelection();
                    }
                    return;
                }
                else {
                    return;
                }
            }
            ele = ele.parentElement;
        }
    };
    return (jsxs("div", { className: "split-diff-view split-diff-view-normal flex w-full basis-[50%]", children: [jsx("style", { "data-select-style": true, ref: ref }), jsx("div", { className: "old-diff-table-wrapper diff-table-scroll-container w-full overflow-x-auto overflow-y-hidden", ref: ref1, style: {
                    // @ts-ignore
                    [diffAsideWidthName]: `${Math.round(width)}px`,
                    overscrollBehaviorX: "none",
                    fontFamily: "Menlo, Consolas, monospace",
                    fontSize: `var(${diffFontSizeName})`,
                }, children: jsx(DiffSplitViewTable, { side: SplitSide.old, diffFile: diffFile, enableAddWidget: !!enableAddWidget, enableHighlight: !!enableHighlight, onMouseDown: onMouseDown }) }), jsx("div", { className: "diff-split-line w-[1.5px]", style: { backgroundColor: `var(${borderColorName})` } }), jsx("div", { className: "new-diff-table-wrapper diff-table-scroll-container w-full overflow-x-auto overflow-y-hidden", ref: ref2, style: {
                    // @ts-ignore
                    [diffAsideWidthName]: `${Math.round(width)}px`,
                    overscrollBehaviorX: "none",
                    fontFamily: "Menlo, Consolas, monospace",
                    fontSize: `var(${diffFontSizeName})`,
                }, children: jsx(DiffSplitViewTable, { side: SplitSide.new, diffFile: diffFile, enableAddWidget: !!enableAddWidget, enableHighlight: !!enableHighlight, onMouseDown: onMouseDown }) })] }));
});
DiffSplitViewNormal.displayName = "DiffSplitViewNormal";

const InternalDiffSplitLine = ({ index, diffFile, lineNumber, enableAddWidget, enableHighlight, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const oldLine = diffFile.getSplitLeftLine(index);
    const newLine = diffFile.getSplitRightLine(index);
    const oldSyntaxLine = diffFile.getOldSyntaxLine((_a = oldLine === null || oldLine === void 0 ? void 0 : oldLine.lineNumber) !== null && _a !== void 0 ? _a : -1);
    const oldPlainLine = diffFile.getOldPlainLine((_b = oldLine.lineNumber) !== null && _b !== void 0 ? _b : -1);
    const newSyntaxLine = diffFile.getNewSyntaxLine((_c = newLine === null || newLine === void 0 ? void 0 : newLine.lineNumber) !== null && _c !== void 0 ? _c : -1);
    const newPlainLine = diffFile.getNewPlainLine((_d = newLine.lineNumber) !== null && _d !== void 0 ? _d : -1);
    const hasDiff = !!(oldLine === null || oldLine === void 0 ? void 0 : oldLine.diff) || !!(newLine === null || newLine === void 0 ? void 0 : newLine.diff);
    const hasChange = checkDiffLineIncludeChange(oldLine === null || oldLine === void 0 ? void 0 : oldLine.diff) || checkDiffLineIncludeChange(newLine === null || newLine === void 0 ? void 0 : newLine.diff);
    const oldLineIsDelete = ((_e = oldLine === null || oldLine === void 0 ? void 0 : oldLine.diff) === null || _e === void 0 ? void 0 : _e.type) === DiffLineType.Delete;
    const newLineIsAdded = ((_f = newLine === null || newLine === void 0 ? void 0 : newLine.diff) === null || _f === void 0 ? void 0 : _f.type) === DiffLineType.Add;
    const { useDiffContext } = useDiffViewContext();
    const onAddWidgetClick = useDiffContext.getReadonlyState().onAddWidgetClick;
    const { useWidget } = useDiffWidgetContext();
    const setWidget = useWidget.getReadonlyState().setWidget;
    const hasOldLine = !!oldLine.lineNumber;
    const hasNewLine = !!newLine.lineNumber;
    const oldLineContentBG = getContentBG(false, oldLineIsDelete, hasDiff);
    const oldLineNumberBG = getLineNumberBG(false, oldLineIsDelete, hasDiff);
    const newLineContentBG = getContentBG(newLineIsAdded, false, hasDiff);
    const newLineNumberBG = getLineNumberBG(newLineIsAdded, false, hasDiff);
    return (jsxs("tr", { "data-line": lineNumber, "data-state": hasDiff ? "diff" : "plain", className: "diff-line", children: [hasOldLine ? (jsxs(Fragment, { children: [jsxs("td", { className: "diff-line-old-num group relative w-[1%] min-w-[40px] select-none pl-[10px] pr-[10px] text-right align-top", "data-side": SplitSide[SplitSide.old], style: {
                            backgroundColor: oldLineNumberBG,
                            color: `var(${hasDiff ? plainLineNumberColorName : expandLineNumberColorName})`,
                        }, children: [hasDiff && enableAddWidget && (jsx(DiffSplitAddWidget, { index: index, lineNumber: (_g = oldLine.lineNumber) !== null && _g !== void 0 ? _g : -1, side: SplitSide.old, diffFile: diffFile, onWidgetClick: (...props) => { var _a; return (_a = onAddWidgetClick.current) === null || _a === void 0 ? void 0 : _a.call(onAddWidgetClick, ...props); }, className: "absolute left-[100%] z-[1] translate-x-[-50%]", onOpenAddWidget: (lineNumber, side) => setWidget({ lineNumber: lineNumber, side: side }) })), jsx("span", { "data-line-num": oldLine.lineNumber, style: { opacity: hasChange ? undefined : 0.5 }, children: oldLine.lineNumber })] }), jsxs("td", { className: "diff-line-old-content group relative pr-[10px] align-top", "data-side": SplitSide[SplitSide.old], style: { backgroundColor: oldLineContentBG }, children: [hasDiff && enableAddWidget && (jsx(DiffSplitAddWidget, { index: index, lineNumber: (_h = oldLine.lineNumber) !== null && _h !== void 0 ? _h : -1, side: SplitSide.old, diffFile: diffFile, onWidgetClick: (...props) => { var _a; return (_a = onAddWidgetClick.current) === null || _a === void 0 ? void 0 : _a.call(onAddWidgetClick, ...props); }, className: "absolute right-[100%] z-[1] translate-x-[50%]", onOpenAddWidget: (lineNumber, side) => setWidget({ lineNumber: lineNumber, side: side }) })), jsx(DiffContent, { enableWrap: true, diffFile: diffFile, rawLine: oldLine.value || "", diffLine: oldLine.diff, plainLine: oldPlainLine, syntaxLine: oldSyntaxLine, enableHighlight: enableHighlight })] })] })) : (jsx("td", { className: "diff-line-old-placeholder select-none", "data-side": SplitSide[SplitSide.old], style: { backgroundColor: `var(${emptyBGName})` }, colSpan: 2, children: jsx("span", { children: "\u2002" }) })), hasNewLine ? (jsxs(Fragment, { children: [jsxs("td", { className: "diff-line-new-num group relative w-[1%] min-w-[40px] select-none border-l-[1px] pl-[10px] pr-[10px] text-right align-top", "data-side": SplitSide[SplitSide.new], style: {
                            backgroundColor: newLineNumberBG,
                            color: `var(${hasDiff ? plainLineNumberColorName : expandLineNumberColorName})`,
                            borderLeftColor: `var(${borderColorName})`,
                            borderLeftStyle: "solid",
                        }, children: [hasDiff && enableAddWidget && (jsx(DiffSplitAddWidget, { index: index, lineNumber: (_j = newLine.lineNumber) !== null && _j !== void 0 ? _j : -1, side: SplitSide.new, diffFile: diffFile, onWidgetClick: (...props) => { var _a; return (_a = onAddWidgetClick.current) === null || _a === void 0 ? void 0 : _a.call(onAddWidgetClick, ...props); }, className: "absolute left-[100%] z-[1] translate-x-[-50%]", onOpenAddWidget: (lineNumber, side) => setWidget({ lineNumber: lineNumber, side: side }) })), jsx("span", { "data-line-num": newLine.lineNumber, style: { opacity: hasChange ? undefined : 0.5 }, children: newLine.lineNumber })] }), jsxs("td", { className: "diff-line-new-content group relative pr-[10px] align-top", "data-side": SplitSide[SplitSide.new], style: { backgroundColor: newLineContentBG }, children: [hasDiff && enableAddWidget && (jsx(DiffSplitAddWidget, { index: index, lineNumber: (_k = newLine.lineNumber) !== null && _k !== void 0 ? _k : -1, side: SplitSide.new, diffFile: diffFile, onWidgetClick: (...props) => { var _a; return (_a = onAddWidgetClick.current) === null || _a === void 0 ? void 0 : _a.call(onAddWidgetClick, ...props); }, className: "absolute right-[100%] z-[1] translate-x-[50%]", onOpenAddWidget: (lineNumber, side) => setWidget({ lineNumber: lineNumber, side: side }) })), jsx(DiffContent, { enableWrap: true, diffFile: diffFile, rawLine: newLine.value || "", diffLine: newLine.diff, plainLine: newPlainLine, syntaxLine: newSyntaxLine, enableHighlight: enableHighlight })] })] })) : (jsx("td", { className: "diff-line-new-placeholder select-none border-l-[1px]", style: {
                    backgroundColor: `var(${emptyBGName})`,
                    borderLeftColor: `var(${borderColorName})`,
                    borderLeftStyle: "solid",
                }, "data-side": SplitSide[SplitSide.new], colSpan: 2, children: jsx("span", { children: "\u2002" }) }))] }));
};
const InternalDiffSplitContentLine = ({ index, diffFile, lineNumber, enableAddWidget, enableHighlight, }) => {
    const oldLine = diffFile.getSplitLeftLine(index);
    const newLine = diffFile.getSplitRightLine(index);
    if ((oldLine === null || oldLine === void 0 ? void 0 : oldLine.isHidden) && (newLine === null || newLine === void 0 ? void 0 : newLine.isHidden))
        return null;
    return (jsx(InternalDiffSplitLine, { index: index, diffFile: diffFile, lineNumber: lineNumber, enableAddWidget: enableAddWidget, enableHighlight: enableHighlight }));
};
const DiffSplitContentLine = memo(InternalDiffSplitContentLine);

const InternalDiffSplitExtendLine = ({ index, diffFile, lineNumber, oldLineExtend, newLineExtend, }) => {
    var _a, _b;
    const { useDiffContext } = useDiffViewContext();
    const oldLine = diffFile.getSplitLeftLine(index);
    const newLine = diffFile.getSplitRightLine(index);
    // 需要显示的时候才进行方法订阅，可以大幅度提高性能
    const renderExtendLine = useDiffContext.useShallowStableSelector((s) => s.renderExtendLine);
    if (!renderExtendLine)
        return null;
    const oldExtendRendered = (oldLineExtend === null || oldLineExtend === void 0 ? void 0 : oldLineExtend.data) &&
        (renderExtendLine === null || renderExtendLine === void 0 ? void 0 : renderExtendLine({
            diffFile,
            side: SplitSide.old,
            lineNumber: (_a = oldLine.lineNumber) !== null && _a !== void 0 ? _a : -1,
            data: oldLineExtend.data,
            onUpdate: diffFile.notifyAll,
        }));
    const newExtendRendered = (newLineExtend === null || newLineExtend === void 0 ? void 0 : newLineExtend.data) &&
        (renderExtendLine === null || renderExtendLine === void 0 ? void 0 : renderExtendLine({
            diffFile,
            side: SplitSide.new,
            lineNumber: (_b = newLine.lineNumber) !== null && _b !== void 0 ? _b : -1,
            data: newLineExtend.data,
            onUpdate: diffFile.notifyAll,
        }));
    return (jsxs("tr", { "data-line": `${lineNumber}-extend`, "data-state": "extend", className: "diff-line diff-line-extend", children: [oldExtendRendered ? (jsx("td", { className: "diff-line-extend-old-content p-0", colSpan: 2, children: jsx("div", { className: "diff-line-extend-wrapper", children: oldExtendRendered }) })) : (jsx("td", { className: "diff-line-extend-old-placeholder select-none p-0", style: { backgroundColor: `var(${emptyBGName})` }, colSpan: 2 })), newExtendRendered ? (jsx("td", { className: "diff-line-extend-new-content border-l-[1px] p-0", style: { borderLeftColor: `var(${borderColorName})`, borderLeftStyle: "solid" }, colSpan: 2, children: jsx("div", { className: "diff-line-extend-wrapper", children: newExtendRendered }) })) : (jsx("td", { className: "diff-line-extend-new-placeholder select-none border-l-[1px] p-0", style: {
                    backgroundColor: `var(${emptyBGName})`,
                    borderLeftColor: `var(${borderColorName})`,
                    borderLeftStyle: "solid",
                }, colSpan: 2 }))] }));
};
const DiffSplitExtendLine = ({ index, diffFile, lineNumber, }) => {
    const { useDiffContext } = useDiffViewContext();
    const oldLine = diffFile.getSplitLeftLine(index);
    const newLine = diffFile.getSplitRightLine(index);
    const { oldLineExtend, newLineExtend } = useDiffContext(React.useCallback((s) => {
        var _a, _b, _c, _d, _e, _f;
        return ({
            oldLineExtend: (_b = (_a = s.extendData) === null || _a === void 0 ? void 0 : _a.oldFile) === null || _b === void 0 ? void 0 : _b[(_c = oldLine === null || oldLine === void 0 ? void 0 : oldLine.lineNumber) !== null && _c !== void 0 ? _c : -1],
            newLineExtend: (_e = (_d = s.extendData) === null || _d === void 0 ? void 0 : _d.newFile) === null || _e === void 0 ? void 0 : _e[(_f = newLine === null || newLine === void 0 ? void 0 : newLine.lineNumber) !== null && _f !== void 0 ? _f : -1],
        });
    }, [oldLine === null || oldLine === void 0 ? void 0 : oldLine.lineNumber, newLine === null || newLine === void 0 ? void 0 : newLine.lineNumber]));
    const hasExtend = (oldLineExtend === null || oldLineExtend === void 0 ? void 0 : oldLineExtend.data) || (newLineExtend === null || newLineExtend === void 0 ? void 0 : newLineExtend.data);
    // if the expand action not enabled, the `isHidden` property will never change
    const enableExpand = diffFile.getExpandEnabled();
    const currentIsShow = hasExtend && ((!(oldLine === null || oldLine === void 0 ? void 0 : oldLine.isHidden) && !(newLine === null || newLine === void 0 ? void 0 : newLine.isHidden)) || !enableExpand);
    if (!currentIsShow)
        return null;
    return (jsx(InternalDiffSplitExtendLine, { index: index, diffFile: diffFile, lineNumber: lineNumber, oldLineExtend: oldLineExtend, newLineExtend: newLineExtend }));
};

const DiffSplitHunkLineGitHub = ({ index, diffFile, lineNumber, }) => {
    var _a;
    const currentHunk = diffFile.getSplitHunkLine(index);
    const expandEnabled = diffFile.getExpandEnabled();
    const couldExpand = expandEnabled && currentHunk && currentHunk.splitInfo;
    const isExpandAll = currentHunk &&
        currentHunk.splitInfo &&
        currentHunk.splitInfo.endHiddenIndex - currentHunk.splitInfo.startHiddenIndex < composeLen;
    const isFirstLine = currentHunk && currentHunk.isFirst;
    const isLastLine = currentHunk && currentHunk.isLast;
    return (jsxs("tr", { "data-line": `${lineNumber}-hunk`, "data-state": "hunk", className: "diff-line diff-line-hunk", children: [jsx("td", { className: "diff-line-hunk-action relative w-[1%] min-w-[40px] select-none p-[1px]", style: {
                    backgroundColor: `var(${hunkLineNumberBGName})`,
                    color: `var(${plainLineNumberColorName})`,
                }, children: couldExpand ? (isFirstLine ? (jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand Up", "data-title": "Expand Up", onClick: () => diffFile.onSplitHunkExpand("up", index), children: jsx(ExpandUp, { className: "fill-current" }) })) : isLastLine ? (jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand Down", "data-title": "Expand Down", onClick: () => diffFile.onSplitHunkExpand("down", index), children: jsx(ExpandDown, { className: "fill-current" }) })) : isExpandAll ? (jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand All", "data-title": "Expand All", onClick: () => diffFile.onSplitHunkExpand("all", index), children: jsx(ExpandAll, { className: "fill-current" }) })) : (jsxs(Fragment, { children: [jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[2px]", title: "Expand Down", "data-title": "Expand Down", onClick: () => diffFile.onSplitHunkExpand("down", index), children: jsx(ExpandDown, { className: "fill-current" }) }), jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[2px]", title: "Expand Up", "data-title": "Expand Up", onClick: () => diffFile.onSplitHunkExpand("up", index), children: jsx(ExpandUp, { className: "fill-current" }) })] }))) : (jsx("div", { className: "min-h-[28px]", children: "\u2002" })) }), jsx("td", { className: "diff-line-hunk-content pr-[10px] align-middle", style: { backgroundColor: `var(${hunkContentBGName})` }, colSpan: 3, children: jsx("div", { className: "pl-[1.5em]", style: {
                        color: `var(${hunkContentColorName})`,
                    }, children: ((_a = currentHunk.splitInfo) === null || _a === void 0 ? void 0 : _a.plainText) || currentHunk.text }) })] }));
};
const DiffSplitHunkLineGitLab = ({ index, diffFile, lineNumber, }) => {
    var _a, _b;
    const currentHunk = diffFile.getSplitHunkLine(index);
    const expandEnabled = diffFile.getExpandEnabled();
    const couldExpand = expandEnabled && currentHunk && currentHunk.splitInfo;
    const isExpandAll = currentHunk &&
        currentHunk.splitInfo &&
        currentHunk.splitInfo.endHiddenIndex - currentHunk.splitInfo.startHiddenIndex < composeLen;
    const isFirstLine = currentHunk && currentHunk.isFirst;
    const isLastLine = currentHunk && currentHunk.isLast;
    return (jsxs("tr", { "data-line": `${lineNumber}-hunk`, "data-state": "hunk", className: "diff-line diff-line-hunk", children: [jsx("td", { className: "diff-line-hunk-action relative w-[1%] min-w-[40px] select-none p-[1px]", style: {
                    backgroundColor: `var(${hunkLineNumberBGName})`,
                    color: `var(${plainLineNumberColorName})`,
                }, children: couldExpand ? (isFirstLine ? (jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand Up", "data-title": "Expand Up", onClick: () => diffFile.onSplitHunkExpand("up", index), children: jsx(ExpandUp, { className: "fill-current" }) })) : isLastLine ? (jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand Down", "data-title": "Expand Down", onClick: () => diffFile.onSplitHunkExpand("down", index), children: jsx(ExpandDown, { className: "fill-current" }) })) : isExpandAll ? (jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand All", "data-title": "Expand All", onClick: () => diffFile.onSplitHunkExpand("all", index), children: jsx(ExpandAll, { className: "fill-current" }) })) : (jsxs(Fragment, { children: [jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[2px]", title: "Expand Down", "data-title": "Expand Down", onClick: () => diffFile.onSplitHunkExpand("down", index), children: jsx(ExpandDown, { className: "fill-current" }) }), jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[2px]", title: "Expand Up", "data-title": "Expand Up", onClick: () => diffFile.onSplitHunkExpand("up", index), children: jsx(ExpandUp, { className: "fill-current" }) })] }))) : (jsx("div", { className: "min-h-[28px]", children: "\u2002" })) }), jsx("td", { className: "diff-line-hunk-content pr-[10px] align-middle", style: { backgroundColor: `var(${hunkContentBGName})` }, children: jsx("div", { className: "pl-[1.5em]", style: {
                        color: `var(${hunkContentColorName})`,
                    }, children: ((_a = currentHunk.splitInfo) === null || _a === void 0 ? void 0 : _a.plainText) || currentHunk.text }) }), jsx("td", { className: "diff-line-hunk-action relative z-[1] w-[1%] min-w-[40px] select-none border-l-[1px] p-[1px]", style: {
                    backgroundColor: `var(${hunkLineNumberBGName})`,
                    color: `var(${plainLineNumberColorName})`,
                    borderLeftColor: `var(${borderColorName})`,
                    borderLeftStyle: "solid",
                }, children: couldExpand ? (isFirstLine ? (jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand Up", "data-title": "Expand Up", onClick: () => diffFile.onSplitHunkExpand("up", index), children: jsx(ExpandUp, { className: "fill-current" }) })) : isLastLine ? (jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand Down", "data-title": "Expand Down", onClick: () => diffFile.onSplitHunkExpand("down", index), children: jsx(ExpandDown, { className: "fill-current" }) })) : isExpandAll ? (jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand All", "data-title": "Expand All", onClick: () => diffFile.onSplitHunkExpand("all", index), children: jsx(ExpandAll, { className: "fill-current" }) })) : (jsxs(Fragment, { children: [jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[2px]", title: "Expand Down", "data-title": "Expand Down", onClick: () => diffFile.onSplitHunkExpand("down", index), children: jsx(ExpandDown, { className: "fill-current" }) }), jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[2px]", title: "Expand Up", "data-title": "Expand Up", onClick: () => diffFile.onSplitHunkExpand("up", index), children: jsx(ExpandUp, { className: "fill-current" }) })] }))) : (jsx("div", { className: "min-h-[28px]", children: "\u2002" })) }), jsx("td", { className: "diff-line-hunk-content relative pr-[10px] align-middle", style: { backgroundColor: `var(${hunkContentBGName})` }, children: jsx("div", { className: "pl-[1.5em]", style: {
                        color: `var(${hunkContentColorName})`,
                    }, children: ((_b = currentHunk.splitInfo) === null || _b === void 0 ? void 0 : _b.plainText) || currentHunk.text }) })] }));
};
const InternalDiffSplitHunkLine = ({ index, diffFile, lineNumber, }) => {
    const { useDiffContext } = useDiffViewContext();
    const diffViewMode = useDiffContext.useShallowStableSelector((s) => s.mode);
    if (diffViewMode === DiffModeEnum.SplitGitHub ||
        diffViewMode === DiffModeEnum.Split ||
        diffViewMode === DiffModeEnum.Unified) {
        return jsx(DiffSplitHunkLineGitHub, { index: index, diffFile: diffFile, lineNumber: lineNumber });
    }
    else {
        return jsx(DiffSplitHunkLineGitLab, { index: index, diffFile: diffFile, lineNumber: lineNumber });
    }
};
const DiffSplitHunkLine = ({ index, diffFile, lineNumber, }) => {
    const currentHunk = diffFile.getSplitHunkLine(index);
    const currentIsShow = currentHunk &&
        currentHunk.splitInfo &&
        currentHunk.splitInfo.startHiddenIndex < currentHunk.splitInfo.endHiddenIndex;
    const currentIsPureHunk = currentHunk && diffFile._getIsPureDiffRender() && !currentHunk.splitInfo;
    if (!currentIsShow && !currentIsPureHunk)
        return null;
    return jsx(InternalDiffSplitHunkLine, { index: index, diffFile: diffFile, lineNumber: lineNumber });
};

const InternalDiffSplitWidgetLine = ({ index, diffFile, lineNumber, }) => {
    var _a, _b;
    const { useWidget } = useDiffWidgetContext();
    const setWidget = useWidget.getReadonlyState().setWidget;
    const { useDiffContext } = useDiffViewContext();
    const renderWidgetLine = useDiffContext.useShallowStableSelector((s) => s.renderWidgetLine);
    const oldLine = diffFile.getSplitLeftLine(index);
    const newLine = diffFile.getSplitRightLine(index);
    const widgetSide = useWidget.useShallowStableSelector((s) => s.widgetSide);
    const widgetLineNumber = useWidget.getReadonlyState().widgetLineNumber;
    const oldLineWidget = oldLine.lineNumber && widgetSide === SplitSide.old && widgetLineNumber === oldLine.lineNumber;
    const newLineWidget = newLine.lineNumber && widgetSide === SplitSide.new && widgetLineNumber === newLine.lineNumber;
    const oldWidgetRendered = oldLineWidget &&
        (renderWidgetLine === null || renderWidgetLine === void 0 ? void 0 : renderWidgetLine({
            diffFile,
            side: SplitSide.old,
            lineNumber: (_a = oldLine.lineNumber) !== null && _a !== void 0 ? _a : -1,
            onClose: () => setWidget({}),
        }));
    const newWidgetRendered = newLineWidget &&
        (renderWidgetLine === null || renderWidgetLine === void 0 ? void 0 : renderWidgetLine({
            diffFile,
            side: SplitSide.new,
            lineNumber: (_b = newLine.lineNumber) !== null && _b !== void 0 ? _b : -1,
            onClose: () => setWidget({}),
        }));
    if (!renderWidgetLine)
        return null;
    return (jsxs("tr", { "data-line": `${lineNumber}-widget`, "data-state": "widget", className: "diff-line diff-line-widget", children: [oldWidgetRendered ? (jsx("td", { className: "diff-line-widget-old-content p-0", colSpan: 2, children: jsx("div", { className: "diff-line-widget-wrapper", children: oldWidgetRendered }) })) : (jsx("td", { className: "diff-line-widget-old-placeholder select-none p-0", style: { backgroundColor: `var(${emptyBGName})` }, colSpan: 2 })), newWidgetRendered ? (jsx("td", { className: "diff-line-widget-new-content border-l-[1px] p-0", colSpan: 2, style: { borderLeftColor: `var(${borderColorName})`, borderLeftStyle: "solid" }, children: jsx("div", { className: "diff-line-widget-wrapper", children: newWidgetRendered }) })) : (jsx("td", { className: "diff-line-widget-new-placeholder select-none border-l-[1px] p-0", style: {
                    backgroundColor: `var(${emptyBGName})`,
                    borderLeftColor: `var(${borderColorName})`,
                    borderLeftStyle: "solid",
                }, colSpan: 2 }))] }));
};
// TODO! improve performance
const DiffSplitWidgetLine = ({ index, diffFile, lineNumber, }) => {
    const { useWidget } = useDiffWidgetContext();
    const currentIsShow = useWidget.useShallowSelector(React.useCallback((s) => {
        const widgetLineNumber = s.widgetLineNumber;
        const widgetSide = s.widgetSide;
        const oldLine = diffFile.getSplitLeftLine(index);
        const newLine = diffFile.getSplitRightLine(index);
        const oldLineWidget = oldLine.lineNumber && widgetSide === SplitSide.old && widgetLineNumber === oldLine.lineNumber;
        const newLineWidget = newLine.lineNumber && widgetSide === SplitSide.new && widgetLineNumber === newLine.lineNumber;
        const currentIsShow = oldLineWidget || newLineWidget;
        return currentIsShow;
    }, [diffFile, index]), (p, c) => p === c);
    if (!currentIsShow)
        return null;
    return jsx(InternalDiffSplitWidgetLine, { index: index, diffFile: diffFile, lineNumber: lineNumber });
};

const DiffSplitViewWrap = memo(({ diffFile }) => {
    const splitLineLength = Math.max(diffFile.splitLineLength, diffFile.fileLineLength);
    const { useDiffContext } = useDiffViewContext();
    const ref = useRef(null);
    const tempRef = useRef(undefined);
    const { fontSize, enableAddWidget, enableHighlight } = useDiffContext.useShallowStableSelector((s) => ({
        fontSize: s.fontSize,
        enableAddWidget: s.enableAddWidget,
        enableHighlight: s.enableHighlight,
    }));
    useSyncExternalStore(diffFile.subscribe, diffFile.getUpdateCount, diffFile.getUpdateCount);
    const font = useMemo(() => ({ fontSize: fontSize + "px", fontFamily: "Menlo, Consolas, monospace" }), [fontSize]);
    const _width = useTextWidth({
        text: splitLineLength.toString(),
        font,
    });
    const width = Math.max(40, _width + 20);
    const lines = getSplitContentLines(diffFile);
    // changes only on content / template rebuilds, not on expand / collapse
    const contentVersion = diffFile.getContentVersion();
    const setStyle = (side) => {
        if (!ref.current)
            return;
        if (!side) {
            ref.current.textContent = "";
        }
        else {
            const id = `diff-root${diffFile.getId()}`;
            const targetSide = side === SplitSide.old ? SplitSide.new : SplitSide.old;
            ref.current.textContent = `#${id} [data-side="${SplitSide[targetSide]}"] {user-select: none} \n#${id} [data-state="extend"] {user-select: none} \n#${id} [data-state="hunk"] {user-select: none} \n#${id} [data-state="widget"] {user-select: none}`;
        }
    };
    const onMouseDown = (e) => {
        let ele = e.target;
        // need remove all the selection
        if (ele && ele instanceof HTMLElement && ele.nodeName === "BUTTON") {
            removeAllSelection();
            return;
        }
        const id = getDiffIdFromElement(ele);
        if (id && id !== `diff-root${diffFile.getId()}`) {
            return;
        }
        while (ele && ele instanceof HTMLElement) {
            const state = ele.getAttribute("data-state");
            const side = ele.getAttribute("data-side");
            if (side) {
                if (tempRef.current !== SplitSide[side]) {
                    tempRef.current = SplitSide[side];
                    setStyle(SplitSide[side]);
                    removeAllSelection();
                }
            }
            if (state) {
                if (state === "extend" || state === "hunk" || state === "widget") {
                    if (tempRef.current !== undefined) {
                        tempRef.current = undefined;
                        setStyle(undefined);
                        removeAllSelection();
                    }
                    return;
                }
                else {
                    return;
                }
            }
            ele = ele.parentElement;
        }
    };
    return (jsx("div", { className: "split-diff-view split-diff-view-wrap w-full", children: jsxs("div", { className: "diff-table-wrapper w-full", style: {
                // @ts-ignore
                [diffAsideWidthName]: `${Math.round(width)}px`,
                fontFamily: "Menlo, Consolas, monospace",
                fontSize: `var(${diffFontSizeName})`,
            }, children: [jsx("style", { "data-select-style": true, ref: ref }), jsxs("table", { className: "diff-table w-full table-fixed border-collapse border-spacing-0", children: [jsxs("colgroup", { children: [jsx("col", { className: "diff-table-old-num-col", width: Math.round(width) }), jsx("col", { className: "diff-table-old-content-col" }), jsx("col", { className: "diff-table-new-num-col", width: Math.round(width) }), jsx("col", { className: "diff-table-new-content-col" })] }), jsx("thead", { className: "hidden", children: jsxs("tr", { children: [jsx("th", { scope: "col", children: "old line number" }), jsx("th", { scope: "col", children: "old line content" }), jsx("th", { scope: "col", children: "new line number" }), jsx("th", { scope: "col", children: "new line content" })] }) }), jsxs("tbody", { className: "diff-table-body leading-[1.6]", onMouseDownCapture: onMouseDown, children: [lines.map((line) => (jsxs(Fragment$1, { children: [jsx(DiffSplitHunkLine, { index: line.index, lineNumber: line.lineNumber, diffFile: diffFile }), jsx(DiffSplitContentLine, { index: line.index, lineNumber: line.lineNumber, diffFile: diffFile, enableAddWidget: !!enableAddWidget, enableHighlight: !!enableHighlight, contentVersion: contentVersion }), jsx(DiffSplitWidgetLine, { index: line.index, lineNumber: line.lineNumber, diffFile: diffFile }), jsx(DiffSplitExtendLine, { index: line.index, lineNumber: line.lineNumber, diffFile: diffFile })] }, line.index))), jsx(DiffSplitHunkLine, { index: diffFile.splitLineLength, lineNumber: diffFile.splitLineLength, diffFile: diffFile })] })] })] }) }));
});
DiffSplitViewWrap.displayName = "DiffSplitViewWrap";

const createDiffConfigStore = (props, diffFileId) => {
    return createStore(() => {
        var _a, _b;
        const dom = ref();
        const setDom = (_dom) => (dom.value = _dom);
        const id = ref(diffFileId);
        const setId = (_id) => (id.value = _id);
        const mode = ref(props.diffViewMode);
        const setMode = (_mode) => (mode.value = _mode);
        const mounted = ref(props.isMounted);
        const setMounted = (_mounted) => (mounted.value = _mounted);
        const enableWrap = ref(props.diffViewWrap);
        const setEnableWrap = (_enableWrap) => (enableWrap.value = _enableWrap);
        const enableAddWidget = ref(props.diffViewAddWidget);
        const setEnableAddWidget = (_enableAddWidget) => (enableAddWidget.value = _enableAddWidget);
        const enableHighlight = ref(props.diffViewHighlight);
        const setEnableHighlight = (_enableHighlight) => (enableHighlight.value = _enableHighlight);
        const fontSize = ref(props.diffViewFontSize);
        const setFontSize = (_fontSize) => (fontSize.value = _fontSize);
        const extendData = ref({
            oldFile: { ...(_a = props.extendData) === null || _a === void 0 ? void 0 : _a.oldFile },
            newFile: { ...(_b = props.extendData) === null || _b === void 0 ? void 0 : _b.newFile },
        });
        const setExtendData = (__extendData) => {
            const _extendData = __extendData || {};
            const existOldKeys = Object.keys(extendData.value.oldFile || {});
            const inComingOldKeys = Object.keys(_extendData.oldFile || {});
            for (const key of existOldKeys) {
                if (!inComingOldKeys.includes(key)) {
                    delete extendData.value.oldFile[key];
                }
            }
            for (const key of inComingOldKeys) {
                extendData.value.oldFile[key] = _extendData.oldFile[key];
            }
            const existNewKeys = Object.keys(extendData.value.newFile || {});
            const inComingNewKeys = Object.keys(_extendData.newFile || {});
            for (const key of existNewKeys) {
                if (!inComingNewKeys.includes(key)) {
                    delete extendData.value.newFile[key];
                }
            }
            for (const key of inComingNewKeys) {
                extendData.value.newFile[key] = _extendData.newFile[key];
            }
        };
        const renderWidgetLine = ref(props.renderWidgetLine);
        const setRenderWidgetLine = (_renderWidgetLine) => (renderWidgetLine.value = _renderWidgetLine);
        const renderExtendLine = ref(props.renderExtendLine);
        const setRenderExtendLine = (_renderExtendLine) => (renderExtendLine.value = _renderExtendLine);
        const onCreateUseWidgetHook = ref(props.onCreateUseWidgetHook);
        const setOnCreateUseWidgetHook = (_onCreateUseWidgetHook) => (onCreateUseWidgetHook.value = _onCreateUseWidgetHook);
        // 避免无意义的订阅
        const onAddWidgetClick = { current: props.onAddWidgetClick };
        const setOnAddWidgetClick = (_onAddWidgetClick) => (onAddWidgetClick.current = _onAddWidgetClick.current);
        return {
            id,
            setId,
            dom,
            setDom,
            mode,
            setMode,
            mounted,
            setMounted,
            enableWrap,
            setEnableWrap,
            enableAddWidget,
            setEnableAddWidget,
            enableHighlight,
            setEnableHighlight,
            fontSize,
            setFontSize,
            extendData,
            setExtendData,
            renderWidgetLine,
            setRenderWidgetLine,
            renderExtendLine,
            setRenderExtendLine,
            onAddWidgetClick,
            setOnAddWidgetClick,
            onCreateUseWidgetHook,
            setOnCreateUseWidgetHook,
        };
    });
};
const createDiffWidgetStore = (useDiffContextRef) => {
    return createStore(() => {
        const widgetSide = ref(undefined);
        const widgetLineNumber = ref(undefined);
        const setWidget = ({ side, lineNumber }) => {
            var _a, _b;
            const { renderWidgetLine } = ((_b = (_a = useDiffContextRef.current) === null || _a === void 0 ? void 0 : _a.getReadonlyState) === null || _b === void 0 ? void 0 : _b.call(_a)) || {};
            if (typeof renderWidgetLine !== "function")
                return;
            widgetSide.value = side;
            widgetLineNumber.value = lineNumber;
        };
        return { widgetSide, widgetLineNumber, setWidget };
    });
};

const DiffSplitView = memo(({ diffFile }) => {
    const { useDiffContext } = useDiffViewContext();
    const useDiffContextRef = useRef(useDiffContext);
    useDiffContextRef.current = useDiffContext;
    const { enableWrap, onCreateUseWidgetHook } = useDiffContext.useShallowStableSelector((s) => ({
        enableWrap: s.enableWrap,
        onCreateUseWidgetHook: s.onCreateUseWidgetHook,
    }));
    // performance optimization
    const useWidget = useMemo(() => createDiffWidgetStore(useDiffContextRef), []);
    const contextValue = useMemo(() => ({ useWidget }), [useWidget]);
    useEffect(() => {
        const { setWidget } = useWidget.getReadonlyState();
        setWidget({});
    }, [diffFile, useWidget]);
    useEffect(() => {
        onCreateUseWidgetHook === null || onCreateUseWidgetHook === void 0 ? void 0 : onCreateUseWidgetHook(useWidget);
    }, [useWidget, onCreateUseWidgetHook]);
    return (jsx(DiffWidgetContext.Provider, { value: contextValue, children: enableWrap ? jsx(DiffSplitViewWrap, { diffFile: diffFile }) : jsx(DiffSplitViewNormal, { diffFile: diffFile }) }));
});
DiffSplitView.displayName = "DiffSplitView";

const DiffUnifiedOldLine = ({ index, diffLine, rawLine, plainLine, syntaxLine, lineNumber, diffFile, setWidget, enableWrap, enableAddWidget, enableHighlight, onAddWidgetClick, }) => {
    return (jsxs("tr", { "data-line": index, "data-state": "diff", className: "diff-line group", children: [jsxs("td", { className: "diff-line-num sticky left-0 z-[1] w-[1%] select-none whitespace-nowrap pl-[5px] pr-[5px] text-right align-top", style: {
                    color: `var(${plainLineNumberColorName})`,
                    backgroundColor: `var(${delLineNumberBGName})`,
                    width: `calc(var(${diffAsideWidthName}) * 2 + 8px)`,
                    maxWidth: `calc(var(${diffAsideWidthName}) * 2 + 8px)`,
                    minWidth: `calc(var(${diffAsideWidthName}) * 2 + 8px)`,
                }, children: [enableAddWidget && (jsx(DiffUnifiedAddWidget, { index: index - 1, lineNumber: lineNumber, diffFile: diffFile, side: SplitSide.old, onWidgetClick: onAddWidgetClick, onOpenAddWidget: (lineNumber, side) => setWidget({ lineNumber, side }) })), jsxs("div", { className: "flex", children: [jsx("span", { "data-line-old-num": lineNumber, className: "inline-block w-[50%]", children: lineNumber }), jsx("span", { className: "w-[8px] shrink-0" }), jsx("span", { className: "inline-block w-[50%]" })] })] }), jsx("td", { className: "diff-line-content pr-[10px] align-top", style: { backgroundColor: `var(${delContentBGName})` }, children: jsx(DiffContent, { enableWrap: enableWrap, diffFile: diffFile, enableHighlight: enableHighlight, rawLine: rawLine, diffLine: diffLine, plainLine: plainLine, syntaxLine: syntaxLine }) })] }));
};
const DiffUnifiedNewLine = ({ index, diffLine, rawLine, plainLine, syntaxLine, lineNumber, diffFile, setWidget, enableWrap, enableAddWidget, enableHighlight, onAddWidgetClick, }) => {
    return (jsxs("tr", { "data-line": index, "data-state": "diff", className: "diff-line group", children: [jsxs("td", { className: "diff-line-num sticky left-0 z-[1] w-[1%] select-none whitespace-nowrap pl-[5px] pr-[5px] text-right align-top", style: {
                    color: `var(${plainLineNumberColorName})`,
                    backgroundColor: `var(${addLineNumberBGName})`,
                    width: `calc(var(${diffAsideWidthName}) * 2 + 8px)`,
                    maxWidth: `calc(var(${diffAsideWidthName}) * 2 + 8px)`,
                    minWidth: `calc(var(${diffAsideWidthName}) * 2 + 8px)`,
                }, children: [enableAddWidget && (jsx(DiffUnifiedAddWidget, { index: index - 1, lineNumber: lineNumber, diffFile: diffFile, side: SplitSide.new, onWidgetClick: onAddWidgetClick, onOpenAddWidget: (lineNumber, side) => setWidget({ lineNumber, side }) })), jsxs("div", { className: "flex", children: [jsx("span", { className: "inline-block w-[50%]" }), jsx("span", { className: "w-[8px] shrink-0" }), jsx("span", { "data-line-new-num": lineNumber, className: "inline-block w-[50%]", children: lineNumber })] })] }), jsx("td", { className: "diff-line-content pr-[10px] align-top", style: { backgroundColor: `var(${addContentBGName})` }, children: jsx(DiffContent, { enableWrap: enableWrap, diffFile: diffFile, enableHighlight: enableHighlight, rawLine: rawLine, diffLine: diffLine, plainLine: plainLine, syntaxLine: syntaxLine }) })] }));
};
const InternalDiffUnifiedLine = ({ index, diffFile, lineNumber, enableWrap, enableAddWidget, enableHighlight, }) => {
    var _a;
    const unifiedLine = diffFile.getUnifiedLine(index);
    const { useDiffContext } = useDiffViewContext();
    const onAddWidgetClick = useDiffContext.getReadonlyState().onAddWidgetClick;
    const { useWidget } = useDiffWidgetContext();
    const setWidget = useWidget.getReadonlyState().setWidget;
    const hasDiff = unifiedLine.diff;
    const hasChange = checkDiffLineIncludeChange(unifiedLine.diff);
    const rawLine = unifiedLine.value || "";
    const diffLine = unifiedLine.diff;
    const newLineNumber = unifiedLine.newLineNumber;
    const oldLinenumber = unifiedLine.oldLineNumber;
    const syntaxLine = newLineNumber
        ? diffFile.getNewSyntaxLine(newLineNumber)
        : oldLinenumber
            ? diffFile.getOldSyntaxLine(oldLinenumber)
            : undefined;
    const plainLine = newLineNumber
        ? diffFile.getNewPlainLine(newLineNumber)
        : oldLinenumber
            ? diffFile.getOldPlainLine(oldLinenumber)
            : undefined;
    if (hasChange) {
        if (unifiedLine.oldLineNumber) {
            return (jsx(DiffUnifiedOldLine, { index: lineNumber, enableWrap: enableWrap, diffFile: diffFile, rawLine: rawLine, diffLine: diffLine, setWidget: setWidget, plainLine: plainLine, syntaxLine: syntaxLine, enableHighlight: enableHighlight, enableAddWidget: enableAddWidget, lineNumber: unifiedLine.oldLineNumber, onAddWidgetClick: (...props) => { var _a; return (_a = onAddWidgetClick.current) === null || _a === void 0 ? void 0 : _a.call(onAddWidgetClick, ...props); } }));
        }
        else {
            return (jsx(DiffUnifiedNewLine, { index: lineNumber, enableWrap: enableWrap, rawLine: rawLine, diffLine: diffLine, diffFile: diffFile, setWidget: setWidget, plainLine: plainLine, syntaxLine: syntaxLine, enableHighlight: enableHighlight, enableAddWidget: enableAddWidget, lineNumber: unifiedLine.newLineNumber, onAddWidgetClick: (...props) => { var _a; return (_a = onAddWidgetClick.current) === null || _a === void 0 ? void 0 : _a.call(onAddWidgetClick, ...props); } }));
        }
    }
    else {
        return (jsxs("tr", { "data-line": lineNumber, "data-state": unifiedLine.diff ? "diff" : "plain", className: "diff-line group", children: [jsxs("td", { className: "diff-line-num sticky left-0 z-[1] w-[1%] select-none whitespace-nowrap pl-[5px] pr-[5px] text-right align-top", style: {
                        color: `var(${hasDiff ? plainLineNumberColorName : expandLineNumberColorName})`,
                        width: `calc(var(${diffAsideWidthName}) * 2 + 8px)`,
                        maxWidth: `calc(var(${diffAsideWidthName}) * 2 + 8px)`,
                        minWidth: `calc(var(${diffAsideWidthName}) * 2 + 8px)`,
                        backgroundColor: hasDiff ? `var(${plainLineNumberBGName})` : `var(${expandContentBGName})`,
                    }, children: [enableAddWidget && hasDiff && (jsx(DiffUnifiedAddWidget, { index: index, diffFile: diffFile, lineNumber: (_a = unifiedLine.newLineNumber) !== null && _a !== void 0 ? _a : -1, side: SplitSide.new, onWidgetClick: (...props) => { var _a; return (_a = onAddWidgetClick.current) === null || _a === void 0 ? void 0 : _a.call(onAddWidgetClick, ...props); }, onOpenAddWidget: (lineNumber, side) => setWidget({ lineNumber, side }) })), jsxs("div", { className: "flex opacity-[0.5]", children: [jsx("span", { "data-line-old-num": unifiedLine.oldLineNumber, className: "inline-block w-[50%]", children: unifiedLine.oldLineNumber }), jsx("span", { className: "w-[8px] shrink-0" }), jsx("span", { "data-line-new-num": unifiedLine.newLineNumber, className: "inline-block w-[50%]", children: unifiedLine.newLineNumber })] })] }), jsx("td", { className: "diff-line-content pr-[10px] align-top", style: {
                        backgroundColor: hasDiff ? `var(${plainContentBGName})` : `var(${expandContentBGName})`,
                    }, children: jsx(DiffContent, { enableWrap: enableWrap, diffFile: diffFile, enableHighlight: enableHighlight, rawLine: rawLine, diffLine: diffLine, plainLine: plainLine, syntaxLine: syntaxLine }) })] }));
    }
};
const InternalDiffUnifiedContentLine = ({ index, diffFile, lineNumber, enableWrap, enableHighlight, enableAddWidget, }) => {
    const unifiedLine = diffFile.getUnifiedLine(index);
    if (unifiedLine === null || unifiedLine === void 0 ? void 0 : unifiedLine.isHidden)
        return null;
    return (jsx(InternalDiffUnifiedLine, { index: index, diffFile: diffFile, lineNumber: lineNumber, enableWrap: enableWrap, enableHighlight: enableHighlight, enableAddWidget: enableAddWidget }));
};
const DiffUnifiedContentLine = memo(InternalDiffUnifiedContentLine);

const InternalDiffUnifiedExtendLine = ({ index, diffFile, lineNumber, enableWrap, oldLineExtend, newLineExtend, }) => {
    var _a, _b;
    const { useDiffContext } = useDiffViewContext();
    const renderExtendLine = useDiffContext.useShallowStableSelector((s) => s.renderExtendLine);
    const unifiedItem = diffFile.getUnifiedLine(index);
    // TODO use css variable to get width
    const width = useDomWidth({
        selector: ".unified-diff-table-wrapper",
        enable: typeof renderExtendLine === "function",
    });
    if (!renderExtendLine)
        return null;
    return (jsx("tr", { "data-line": `${lineNumber}-extend`, "data-state": "extend", className: "diff-line diff-line-extend", children: jsx("td", { className: "diff-line-extend-content p-0 align-top", colSpan: 2, children: jsxs("div", { className: "diff-line-extend-wrapper sticky left-0 z-[1]", style: { width }, children: [(enableWrap ? true : width > 0) &&
                        (oldLineExtend === null || oldLineExtend === void 0 ? void 0 : oldLineExtend.data) !== undefined &&
                        (oldLineExtend === null || oldLineExtend === void 0 ? void 0 : oldLineExtend.data) !== null &&
                        (renderExtendLine === null || renderExtendLine === void 0 ? void 0 : renderExtendLine({
                            diffFile,
                            side: SplitSide.old,
                            lineNumber: (_a = unifiedItem.oldLineNumber) !== null && _a !== void 0 ? _a : -1,
                            data: oldLineExtend.data,
                            onUpdate: diffFile.notifyAll,
                        })), (enableWrap ? true : width > 0) &&
                        (newLineExtend === null || newLineExtend === void 0 ? void 0 : newLineExtend.data) !== undefined &&
                        (newLineExtend === null || newLineExtend === void 0 ? void 0 : newLineExtend.data) !== null &&
                        (renderExtendLine === null || renderExtendLine === void 0 ? void 0 : renderExtendLine({
                            diffFile,
                            side: SplitSide.new,
                            lineNumber: (_b = unifiedItem.newLineNumber) !== null && _b !== void 0 ? _b : -1,
                            data: newLineExtend.data,
                            onUpdate: diffFile.notifyAll,
                        }))] }) }) }));
};
const DiffUnifiedExtendLine = ({ index, diffFile, lineNumber, enableWrap, }) => {
    const { useDiffContext } = useDiffViewContext();
    const unifiedItem = diffFile.getUnifiedLine(index);
    const { oldLineExtend, newLineExtend } = useDiffContext(useCallback((s) => {
        var _a, _b, _c, _d, _e, _f;
        return ({
            oldLineExtend: (_b = (_a = s.extendData) === null || _a === void 0 ? void 0 : _a.oldFile) === null || _b === void 0 ? void 0 : _b[(_c = unifiedItem === null || unifiedItem === void 0 ? void 0 : unifiedItem.oldLineNumber) !== null && _c !== void 0 ? _c : -1],
            newLineExtend: (_e = (_d = s.extendData) === null || _d === void 0 ? void 0 : _d.newFile) === null || _e === void 0 ? void 0 : _e[(_f = unifiedItem === null || unifiedItem === void 0 ? void 0 : unifiedItem.newLineNumber) !== null && _f !== void 0 ? _f : -1],
        });
    }, [unifiedItem.oldLineNumber, unifiedItem.newLineNumber]));
    const hasExtend = (oldLineExtend === null || oldLineExtend === void 0 ? void 0 : oldLineExtend.data) || (newLineExtend === null || newLineExtend === void 0 ? void 0 : newLineExtend.data);
    if (!hasExtend || !unifiedItem || unifiedItem.isHidden)
        return null;
    return (jsx(InternalDiffUnifiedExtendLine, { index: index, diffFile: diffFile, lineNumber: lineNumber, enableWrap: enableWrap, oldLineExtend: oldLineExtend, newLineExtend: newLineExtend }));
};

const InternalDiffUnifiedHunkLine = ({ index, diffFile, lineNumber, }) => {
    var _a;
    const currentHunk = diffFile.getUnifiedHunkLine(index);
    const expandEnabled = diffFile.getExpandEnabled();
    const { useDiffContext } = useDiffViewContext();
    const enableWrap = useDiffContext.useShallowStableSelector((s) => s.enableWrap);
    const couldExpand = expandEnabled && currentHunk && currentHunk.unifiedInfo;
    const isExpandAll = currentHunk &&
        currentHunk.unifiedInfo &&
        currentHunk.unifiedInfo.endHiddenIndex - currentHunk.unifiedInfo.startHiddenIndex < composeLen;
    const isFirstLine = currentHunk && currentHunk.isFirst;
    const isLastLine = currentHunk && currentHunk.isLast;
    return (jsxs("tr", { "data-line": `${lineNumber}-hunk`, "data-state": "hunk", className: "diff-line diff-line-hunk", children: [jsx("td", { className: "diff-line-hunk-action sticky left-0 w-[1%] select-none p-[1px]", style: {
                    backgroundColor: `var(${hunkLineNumberBGName})`,
                    color: `var(${plainLineNumberColorName})`,
                    width: `calc(var(${diffAsideWidthName}) * 2 + 8px)`,
                    maxWidth: `calc(var(${diffAsideWidthName}) * 2 + 8px)`,
                    minWidth: `calc(var(${diffAsideWidthName}) * 2 + 8px)`,
                }, children: couldExpand ? (isFirstLine ? (jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand Up", "data-title": "Expand Up", onClick: () => diffFile.onUnifiedHunkExpand("up", index), children: jsx(ExpandUp, { className: "fill-current" }) })) : isLastLine ? (jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand Down", "data-title": "Expand Down", onClick: () => diffFile.onUnifiedHunkExpand("down", index), children: jsx(ExpandDown, { className: "fill-current" }) })) : isExpandAll ? (jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[6px]", title: "Expand All", "data-title": "Expand All", onClick: () => diffFile.onUnifiedHunkExpand("all", index), children: jsx(ExpandAll, { className: "fill-current" }) })) : (jsxs(Fragment, { children: [jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[2px]", title: "Expand Down", "data-title": "Expand Down", onClick: () => diffFile.onUnifiedHunkExpand("down", index), children: jsx(ExpandDown, { className: "fill-current" }) }), jsx("button", { className: "diff-widget-tooltip flex w-full cursor-pointer items-center justify-center rounded-[2px] py-[2px]", title: "Expand Up", "data-title": "Expand Up", onClick: () => diffFile.onUnifiedHunkExpand("up", index), children: jsx(ExpandUp, { className: "fill-current" }) })] }))) : (jsx("div", { className: "min-h-[28px]", children: "\u2002" })) }), jsx("td", { className: "diff-line-hunk-content pr-[10px] align-middle", style: { backgroundColor: `var(${hunkContentBGName})` }, children: jsx("div", { className: "pl-[1.5em]", style: {
                        whiteSpace: enableWrap ? "pre-wrap" : "pre",
                        wordBreak: enableWrap ? "break-all" : "initial",
                        color: `var(${hunkContentColorName})`,
                    }, children: ((_a = currentHunk.unifiedInfo) === null || _a === void 0 ? void 0 : _a.plainText) || currentHunk.text }) })] }));
};
const DiffUnifiedHunkLine = ({ index, diffFile, lineNumber, }) => {
    const currentHunk = diffFile.getUnifiedHunkLine(index);
    const currentIsShow = currentHunk &&
        currentHunk.unifiedInfo &&
        currentHunk.unifiedInfo.startHiddenIndex < currentHunk.unifiedInfo.endHiddenIndex;
    const currentIsPureHunk = currentHunk && diffFile._getIsPureDiffRender() && !currentHunk.unifiedInfo;
    if (!currentIsShow && !currentIsPureHunk)
        return null;
    return jsx(InternalDiffUnifiedHunkLine, { index: index, diffFile: diffFile, lineNumber: lineNumber });
};

const InternalDiffUnifiedWidgetLine = ({ index, diffFile, lineNumber, enableWrap, }) => {
    var _a, _b;
    const { useWidget } = useDiffWidgetContext();
    const setWidget = useWidget.getReadonlyState().setWidget;
    const unifiedItem = diffFile.getUnifiedLine(index);
    const onClose = () => setWidget({});
    const widgetSide = useWidget.getReadonlyState().widgetSide;
    const widgetLineNumber = useWidget.getReadonlyState().widgetLineNumber;
    const oldWidget = unifiedItem.oldLineNumber && widgetSide === SplitSide.old && widgetLineNumber === unifiedItem.oldLineNumber;
    const newWidget = unifiedItem.newLineNumber && widgetSide === SplitSide.new && widgetLineNumber === unifiedItem.newLineNumber;
    const { useDiffContext } = useDiffViewContext();
    // 需要显示的时候才进行方法订阅，可以大幅度提高性能
    const renderWidgetLine = useDiffContext.useShallowStableSelector((s) => s.renderWidgetLine);
    const width = useDomWidth({
        selector: ".unified-diff-table-wrapper",
        enable: typeof renderWidgetLine === "function",
    });
    if (!renderWidgetLine)
        return null;
    return (jsx("tr", { "data-line": `${lineNumber}-widget`, "data-state": "widget", className: "diff-line diff-line-widget", children: jsx("td", { className: "diff-line-widget-content p-0", colSpan: 2, children: jsxs("div", { className: "diff-line-widget-wrapper sticky left-0 z-[1]", style: { width }, children: [(enableWrap ? true : width > 0) &&
                        oldWidget &&
                        (renderWidgetLine === null || renderWidgetLine === void 0 ? void 0 : renderWidgetLine({ diffFile, side: SplitSide.old, lineNumber: (_a = unifiedItem.oldLineNumber) !== null && _a !== void 0 ? _a : -1, onClose })), (enableWrap ? true : width > 0) &&
                        newWidget &&
                        (renderWidgetLine === null || renderWidgetLine === void 0 ? void 0 : renderWidgetLine({ diffFile, side: SplitSide.new, lineNumber: (_b = unifiedItem.newLineNumber) !== null && _b !== void 0 ? _b : -1, onClose }))] }) }) }));
};
const DiffUnifiedWidgetLine = ({ index, diffFile, lineNumber, enableWrap, }) => {
    const { useWidget } = useDiffWidgetContext();
    const currentIsShow = useWidget.useShallowSelector(React.useCallback((s) => {
        const widgetLineNumber = s.widgetLineNumber;
        const widgetSide = s.widgetSide;
        const unifiedItem = diffFile.getUnifiedLine(index);
        const oldWidget = unifiedItem.oldLineNumber && widgetSide === SplitSide.old && widgetLineNumber === unifiedItem.oldLineNumber;
        const newWidget = unifiedItem.newLineNumber && widgetSide === SplitSide.new && widgetLineNumber === unifiedItem.newLineNumber;
        const currentIsShow = oldWidget || newWidget;
        return currentIsShow;
    }, [diffFile, index]), (p, c) => p === c);
    if (!currentIsShow)
        return null;
    return (jsx(InternalDiffUnifiedWidgetLine, { index: index, diffFile: diffFile, lineNumber: lineNumber, enableWrap: enableWrap }));
};

const DiffUnifiedView = memo(({ diffFile }) => {
    const { useDiffContext } = useDiffViewContext();
    const ref = useRef(null);
    const tempRef = useRef(undefined);
    const useDiffContextRef = useRef(useDiffContext);
    useDiffContextRef.current = useDiffContext;
    // performance optimization
    const useWidget = useMemo(() => createDiffWidgetStore(useDiffContextRef), []);
    const contextValue = useMemo(() => ({ useWidget }), [useWidget]);
    const { fontSize, enableWrap, enableHighlight, enableAddWidget, onCreateUseWidgetHook } = useDiffContext.useShallowStableSelector((s) => ({
        fontSize: s.fontSize,
        enableWrap: s.enableWrap,
        enableHighlight: s.enableHighlight,
        enableAddWidget: s.enableAddWidget,
        onCreateUseWidgetHook: s.onCreateUseWidgetHook,
    }));
    useSyncExternalStore(diffFile.subscribe, diffFile.getUpdateCount, diffFile.getUpdateCount);
    useEffect(() => {
        const { setWidget } = useWidget.getReadonlyState();
        setWidget({});
    }, [diffFile, useWidget]);
    useEffect(() => {
        onCreateUseWidgetHook === null || onCreateUseWidgetHook === void 0 ? void 0 : onCreateUseWidgetHook(useWidget);
    }, [useWidget, onCreateUseWidgetHook]);
    const unifiedLineLength = Math.max(diffFile.unifiedLineLength, diffFile.fileLineLength);
    const _width = useTextWidth({
        text: unifiedLineLength.toString(),
        font: useMemo(() => ({ fontSize: fontSize + "px", fontFamily: "Menlo, Consolas, monospace" }), [fontSize]),
    });
    const width = Math.max(20, _width + 5);
    const lines = getUnifiedContentLine(diffFile);
    // changes only on content / template rebuilds, not on expand / collapse
    const contentVersion = diffFile.getContentVersion();
    const setStyle = (side) => {
        if (!ref.current)
            return;
        if (!side) {
            ref.current.textContent = "";
        }
        else {
            const id = `diff-root${diffFile.getId()}`;
            ref.current.textContent = `#${id} [data-state="extend"] {user-select: none} \n#${id} [data-state="hunk"] {user-select: none} \n#${id} [data-state="widget"] {user-select: none}`;
        }
    };
    const onMouseDown = (e) => {
        let ele = e.target;
        // need remove all the selection
        if (ele && ele instanceof HTMLElement && ele.nodeName === "BUTTON") {
            removeAllSelection();
            return;
        }
        const id = getDiffIdFromElement(ele);
        if (id && id !== `diff-root${diffFile.getId()}`) {
            return;
        }
        while (ele && ele instanceof HTMLElement) {
            const state = ele.getAttribute("data-state");
            if (state) {
                if (state === "extend" || state === "hunk" || state === "widget") {
                    if (tempRef.current !== undefined) {
                        tempRef.current = undefined;
                        setStyle(undefined);
                        removeAllSelection();
                    }
                    return;
                }
                else {
                    if (tempRef.current !== SplitSide.new) {
                        tempRef.current = SplitSide.new;
                        setStyle(SplitSide.new);
                        removeAllSelection();
                    }
                    return;
                }
            }
            ele = ele.parentElement;
        }
    };
    return (jsx(DiffWidgetContext.Provider, { value: contextValue, children: jsxs("div", { className: `unified-diff-view ${enableWrap ? "unified-diff-view-wrap" : "unified-diff-view-normal"} w-full`, children: [jsx("style", { "data-select-style": true, ref: ref }), jsx("div", { className: "unified-diff-table-wrapper diff-table-scroll-container w-full overflow-x-auto overflow-y-hidden", style: {
                        // @ts-ignore
                        [diffAsideWidthName]: `${Math.round(width)}px`,
                        fontFamily: "Menlo, Consolas, monospace",
                        fontSize: `var(${diffFontSizeName})`,
                    }, children: jsxs("table", { className: `unified-diff-table w-full border-collapse border-spacing-0 ${enableWrap ? "table-fixed" : ""}`, children: [jsxs("colgroup", { children: [jsx("col", { className: "unified-diff-table-num-col" }), jsx("col", { className: "unified-diff-table-content-col" })] }), jsx("thead", { className: "hidden", children: jsxs("tr", { children: [jsx("th", { scope: "col", children: "line number" }), jsx("th", { scope: "col", children: "line content" })] }) }), jsxs("tbody", { className: "diff-table-body leading-[1.6]", onMouseDownCapture: onMouseDown, children: [lines.map((item) => (jsxs(Fragment$1, { children: [jsx(DiffUnifiedHunkLine, { index: item.index, lineNumber: item.lineNumber, diffFile: diffFile }), jsx(DiffUnifiedContentLine, { index: item.index, lineNumber: item.lineNumber, diffFile: diffFile, enableWrap: !!enableWrap, enableHighlight: !!enableHighlight, enableAddWidget: !!enableAddWidget, contentVersion: contentVersion }), jsx(DiffUnifiedWidgetLine, { index: item.index, lineNumber: item.lineNumber, diffFile: diffFile, enableWrap: !!enableWrap }), jsx(DiffUnifiedExtendLine, { index: item.index, lineNumber: item.lineNumber, diffFile: diffFile, enableWrap: !!enableWrap })] }, item.index))), jsx(DiffUnifiedHunkLine, { index: diffFile.unifiedLineLength, lineNumber: diffFile.unifiedLineLength, diffFile: diffFile })] })] }) })] }) }));
});
DiffUnifiedView.displayName = "DiffUnifiedView";

_cacheMap.name = "@git-diff-view/react";
const InternalDiffView = (props) => {
    const { diffFile, className, style, wrapperRef, diffViewMode, diffViewWrap, diffViewFontSize, diffViewHighlight, renderWidgetLine, renderExtendLine, extendData, diffViewAddWidget, onAddWidgetClick, onCreateUseWidgetHook, isMounted, } = props;
    const diffFileId = useMemo(() => diffFile.getId(), [diffFile]);
    // performance optimization
    const useDiffContext = useMemo(() => createDiffConfigStore(props, diffFileId), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    useEffect(() => {
        const { id, setId, mode, setMode, mounted, setMounted, enableAddWidget, setEnableAddWidget, enableHighlight, setEnableHighlight, enableWrap, setEnableWrap, setExtendData, fontSize, setFontSize, onAddWidgetClick: _onAddWidgetClick, setOnAddWidgetClick, renderExtendLine: _renderExtendLine, setRenderExtendLine, renderWidgetLine: _renderWidgetLine, setRenderWidgetLine, onCreateUseWidgetHook: _onCreateUseWidgetHook, setOnCreateUseWidgetHook, } = useDiffContext.getReadonlyState();
        if (diffFileId && diffFileId !== id) {
            setId(diffFileId);
        }
        if (diffViewMode && diffViewMode !== mode) {
            setMode(diffViewMode);
        }
        if (mounted !== isMounted) {
            setMounted(isMounted);
        }
        if (diffViewAddWidget !== enableAddWidget) {
            setEnableAddWidget(!!diffViewAddWidget);
        }
        if (diffViewHighlight !== enableHighlight) {
            setEnableHighlight(!!diffViewHighlight);
        }
        if (diffViewWrap !== enableWrap) {
            setEnableWrap(!!diffViewWrap);
        }
        if (extendData) {
            setExtendData(extendData);
        }
        if (diffViewFontSize && diffViewFontSize !== fontSize) {
            setFontSize(diffViewFontSize);
        }
        if (onAddWidgetClick !== _onAddWidgetClick.current) {
            setOnAddWidgetClick({ current: onAddWidgetClick });
        }
        if (onCreateUseWidgetHook !== _onCreateUseWidgetHook) {
            setOnCreateUseWidgetHook(onCreateUseWidgetHook);
        }
        if (renderExtendLine !== _renderExtendLine) {
            setRenderExtendLine(renderExtendLine);
        }
        if (renderWidgetLine !== _renderWidgetLine) {
            setRenderWidgetLine(renderWidgetLine);
        }
    }, [
        useDiffContext,
        diffViewFontSize,
        diffViewHighlight,
        diffViewMode,
        diffViewWrap,
        diffViewAddWidget,
        diffFileId,
        isMounted,
        renderWidgetLine,
        renderExtendLine,
        extendData,
        onAddWidgetClick,
        onCreateUseWidgetHook,
    ]);
    useEffect(() => {
        if (wrapperRef === null || wrapperRef === void 0 ? void 0 : wrapperRef.current) {
            useDiffContext.getReadonlyState().setDom(wrapperRef === null || wrapperRef === void 0 ? void 0 : wrapperRef.current);
        }
    }, [useDiffContext, wrapperRef]);
    const value = useMemo(() => ({ useDiffContext }), [useDiffContext]);
    return (jsx(DiffViewContext.Provider, { value: value, children: jsx("div", { className: "diff-tailwindcss-wrapper", "data-component": "git-diff-view", "data-theme": diffFile._getTheme() || "light", "data-version": "0.1.8", "data-highlighter": diffFile._getHighlighterName(), ref: wrapperRef, children: jsx("div", { className: "diff-style-root", style: {
                    // @ts-ignore
                    [diffFontSizeName]: diffViewFontSize + "px",
                }, children: jsx("div", { id: isMounted ? `diff-root${diffFileId}` : undefined, className: "diff-view-wrapper" + (className ? ` ${className}` : ""), style: style, children: diffViewMode & DiffModeEnum.Split ? (jsx(DiffSplitView, { diffFile: diffFile })) : (jsx(DiffUnifiedView, { diffFile: diffFile })) }) }) }) }));
};
const MemoedInternalDiffView = memo(InternalDiffView);
const DiffViewWithRef = (props, ref) => {
    const { registerHighlighter, data, diffViewTheme, diffFile: _diffFile, ...restProps } = props;
    const diffFile = useMemo(() => {
        var _a, _b, _c, _d, _e, _f;
        if (_diffFile) {
            // missing data for plain file render
            // TODO next release update ?
            // will cause more complex for diffFile flow control, keep current
            const diffFile = DiffFile.createInstance({});
            diffFile._mergeFullBundle(_diffFile._getFullBundle());
            return diffFile;
        }
        else if (data) {
            return new DiffFile(((_a = data === null || data === void 0 ? void 0 : data.oldFile) === null || _a === void 0 ? void 0 : _a.fileName) || "", ((_b = data === null || data === void 0 ? void 0 : data.oldFile) === null || _b === void 0 ? void 0 : _b.content) || "", ((_c = data === null || data === void 0 ? void 0 : data.newFile) === null || _c === void 0 ? void 0 : _c.fileName) || "", ((_d = data === null || data === void 0 ? void 0 : data.newFile) === null || _d === void 0 ? void 0 : _d.content) || "", (data === null || data === void 0 ? void 0 : data.hunks) || [], ((_e = data === null || data === void 0 ? void 0 : data.oldFile) === null || _e === void 0 ? void 0 : _e.fileLang) || "", ((_f = data === null || data === void 0 ? void 0 : data.newFile) === null || _f === void 0 ? void 0 : _f.fileLang) || "");
        }
        return null;
    }, [data, _diffFile]);
    const wrapperRef = useRef(null);
    const isMounted = useIsMounted();
    useEffect(() => {
        if (_diffFile && diffFile) {
            _diffFile._addClonedInstance(diffFile);
            return () => {
                _diffFile._delClonedInstance(diffFile);
            };
        }
    }, [diffFile, _diffFile]);
    useEffect(() => {
        if (!diffFile)
            return;
        diffFile.initTheme(diffViewTheme);
        diffFile.initRaw();
        diffFile.buildSplitDiffLines();
        diffFile.buildUnifiedDiffLines();
    }, [diffFile, diffViewTheme]);
    useEffect(() => {
        if (!diffFile)
            return;
        if (props.diffViewHighlight) {
            if (registerHighlighter) {
                if (registerHighlighter.name !== diffFile._getHighlighterName() ||
                    registerHighlighter.type !== diffFile._getHighlighterType() ||
                    registerHighlighter.type !== "class") {
                    diffFile.initSyntax({ registerHighlighter: registerHighlighter });
                    diffFile.notifyAll();
                }
            }
            else if ((!diffFile._getIsCloned() && diffFile._getHighlighterName() !== highlighter.name) ||
                diffFile._getHighlighterType() !== "class") {
                diffFile.initSyntax();
                diffFile.notifyAll();
            }
        }
    }, [diffFile, props.diffViewHighlight, registerHighlighter, diffViewTheme]);
    useEffect(() => {
        if (!diffFile)
            return;
        const init = () => {
            var _a, _b;
            (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.setAttribute("data-theme", diffFile._getTheme() || "light");
            (_b = wrapperRef.current) === null || _b === void 0 ? void 0 : _b.setAttribute("data-highlighter", diffFile._getHighlighterName());
        };
        init();
        const cb = diffFile.subscribe(init);
        return cb;
    }, [diffFile, diffViewTheme]);
    // fix react strict mode error
    useEffect(() => {
        return () => { var _a, _b; return (process.env.NODE_ENV === "development" ? (_a = diffFile === null || diffFile === void 0 ? void 0 : diffFile._destroy) === null || _a === void 0 ? void 0 : _a.call(diffFile) : (_b = diffFile === null || diffFile === void 0 ? void 0 : diffFile.clear) === null || _b === void 0 ? void 0 : _b.call(diffFile)); };
    }, [diffFile]);
    useImperativeHandle(ref, () => ({ getDiffFileInstance: () => diffFile }), [diffFile]);
    if (!diffFile)
        return null;
    return (jsx(MemoedInternalDiffView, { ...restProps, diffFile: diffFile, isMounted: isMounted, wrapperRef: wrapperRef, diffViewTheme: diffViewTheme, diffViewMode: restProps.diffViewMode || DiffModeEnum.SplitGitHub, diffViewFontSize: restProps.diffViewFontSize || 14 }, diffFile.getId()));
};
const InnerDiffView = forwardRef(DiffViewWithRef);
InnerDiffView.displayName = "DiffView";
const DiffView = InnerDiffView;
const version = "0.1.8";

/* eslint-disable @typescript-eslint/no-unsafe-function-type */
const useCallbackRef = (cb) => {
    const cbRef = useRef(cb);
    // eslint-disable-next-line react-hooks/refs
    cbRef.current = cb;
    return useCallback((...args) => {
        var _a;
        return (_a = cbRef.current) === null || _a === void 0 ? void 0 : _a.call(cbRef, ...args);
    }, []);
};

const useUpdateEffect = (effect, deps) => {
    const isMounted = useRef(false);
    useEffect(() => {
        if (isMounted.current) {
            return effect();
        }
        isMounted.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
};

/* eslint-disable @typescript-eslint/no-unnecessary-type-constraint */
const InternalDiffViewWithMultiSelect = (props, ref) => {
    const { enableMultiSelect = true, extendData, onMultiSelectComplete, onMultiSelectChange, scopeMultiSelectToHunk, renderWidgetLine, onAddWidgetClick, diffViewMode = DiffModeEnum.SplitGitHub, ...restProps } = props;
    const memoSelectChange = useCallbackRef(onMultiSelectChange);
    const memoSelectComplete = useCallbackRef(onMultiSelectComplete);
    const memoScopeSelectToHunk = useCallbackRef(scopeMultiSelectToHunk);
    const containerRef = useRef(null);
    const diffViewRef = useRef(null);
    const managerRef = useRef(null);
    const multiResultRef = useRef(undefined);
    const isUnifiedMode = !(diffViewMode & DiffModeEnum.Split);
    const updateMultiResult = useCallback((result) => {
        var _a;
        multiResultRef.current = result;
        (_a = managerRef.current) === null || _a === void 0 ? void 0 : _a.setPreselectedLines(result || { old: [], new: [] });
    }, []);
    useUpdateEffect(() => {
        updateMultiResult(undefined);
    }, [props.diffViewWrap, diffViewMode]);
    const getDiffFile = useCallback(() => {
        var _a, _b;
        return (_b = (_a = diffViewRef.current) === null || _a === void 0 ? void 0 : _a.getDiffFileInstance()) !== null && _b !== void 0 ? _b : null;
    }, []);
    useEffect(() => {
        var _a;
        const container = containerRef.current;
        const diffFile = getDiffFile();
        if (!container || !diffFile || !enableMultiSelect) {
            (_a = managerRef.current) === null || _a === void 0 ? void 0 : _a.destroy();
            managerRef.current = null;
            return;
        }
        const managerOptions = {
            enabled: enableMultiSelect,
            isUnifiedMode,
            selectedClassName: multiSelectClassNames.selected,
            onSelectionChange: (range, state) => {
                var _a, _b;
                if (state.isSelecting) {
                    (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.classList.add(multiSelectClassNames.selecting);
                }
                else {
                    (_b = containerRef.current) === null || _b === void 0 ? void 0 : _b.classList.remove(multiSelectClassNames.selecting);
                }
                if (state.isSelecting && multiResultRef.current) {
                    updateMultiResult(undefined);
                }
                memoSelectChange === null || memoSelectChange === void 0 ? void 0 : memoSelectChange(range, state);
            },
            onSelectionComplete: (result) => {
                var _a;
                (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.classList.remove(multiSelectClassNames.selecting);
                if (result && result.lines.length > 0) {
                    memoSelectComplete === null || memoSelectComplete === void 0 ? void 0 : memoSelectComplete(result);
                    const finalResult = {
                        [result.range.side]: [result.range.startLineNumber, result.range.endLineNumber],
                    };
                    updateMultiResult(finalResult);
                }
                else {
                    updateMultiResult(undefined);
                }
            },
            scopeToHunk: memoScopeSelectToHunk,
        };
        if (managerRef.current) {
            managerRef.current.updateContainer(container);
            managerRef.current.updateDiffFile(diffFile);
            managerRef.current.updateOptions(managerOptions);
        }
        else {
            managerRef.current = createDiffMultiSelectManager(container, diffFile, managerOptions);
        }
        return () => {
            var _a;
            (_a = managerRef.current) === null || _a === void 0 ? void 0 : _a.destroy();
            managerRef.current = null;
        };
    }, [
        enableMultiSelect,
        isUnifiedMode,
        memoScopeSelectToHunk,
        memoSelectChange,
        memoSelectComplete,
        getDiffFile,
        updateMultiResult,
    ]);
    const convertedExtendData = useMemo(() => {
        if (!extendData)
            return undefined;
        const result = {};
        if (extendData.oldFile) {
            result.oldFile = {};
            for (const [key, value] of Object.entries(extendData.oldFile)) {
                result.oldFile[key] = { data: value.data };
            }
        }
        if (extendData.newFile) {
            result.newFile = {};
            for (const [key, value] of Object.entries(extendData.newFile)) {
                result.newFile[key] = { data: value.data };
            }
        }
        return result;
    }, [extendData]);
    const internalRenderWidgetLine = useCallback(({ lineNumber, side, diffFile, onClose, }) => {
        var _a;
        if (!renderWidgetLine)
            return null;
        const sideKey = side === SplitSide.old ? "old" : "new";
        const multiResultItem = (_a = multiResultRef.current) === null || _a === void 0 ? void 0 : _a[sideKey];
        const fromLineNumber = multiResultItem ? Math.min(...multiResultItem) : lineNumber;
        const toLineNumber = multiResultItem ? Math.max(...multiResultItem) : lineNumber;
        return renderWidgetLine({
            lineNumber: toLineNumber,
            fromLineNumber,
            side,
            diffFile,
            onClose,
        });
    }, [renderWidgetLine]);
    const getSelectionResult = useCallback(() => {
        var _a, _b;
        return (_b = (_a = managerRef.current) === null || _a === void 0 ? void 0 : _a.getSelectionResult()) !== null && _b !== void 0 ? _b : null;
    }, []);
    const getSelectionState = useCallback(() => {
        var _a, _b;
        return ((_b = (_a = managerRef.current) === null || _a === void 0 ? void 0 : _a.getState()) !== null && _b !== void 0 ? _b : {
            isSelecting: false,
            startInfo: null,
            currentRange: null,
        });
    }, []);
    const clearSelection = useCallback(() => {
        var _a;
        (_a = managerRef.current) === null || _a === void 0 ? void 0 : _a.clearSelection();
    }, []);
    const setPreselectedLines = updateMultiResult;
    useImperativeHandle(ref, () => ({
        getDiffFileInstance: getDiffFile,
        getSelectionResult,
        getSelectionState,
        clearSelection,
        setPreselectedLines,
    }), [getDiffFile, getSelectionResult, getSelectionState, clearSelection, setPreselectedLines]);
    return (jsx("div", { ref: containerRef, className: "diff-multiselect-wrapper", children: jsx(DiffView, { ref: diffViewRef, ...restProps, diffViewMode: diffViewMode, extendData: convertedExtendData, onAddWidgetClick: (lineNum, side) => {
                var _a, _b;
                (_a = managerRef.current) === null || _a === void 0 ? void 0 : _a.clearSelection();
                const multiResult = multiResultRef.current;
                if (multiResult) {
                    const currentSide = SplitSide[side];
                    const currentMultiResult = multiResult[currentSide];
                    const otherSide = currentSide === "new" ? "old" : "new";
                    const otherMultiResult = multiResult[otherSide];
                    if (currentMultiResult === null || currentMultiResult === void 0 ? void 0 : currentMultiResult.length) {
                        const max = Math.max(...currentMultiResult);
                        if (max === lineNum) {
                            const finalResult = { [currentSide]: currentMultiResult };
                            updateMultiResult(finalResult);
                            onAddWidgetClick === null || onAddWidgetClick === void 0 ? void 0 : onAddWidgetClick({ lineNumber: max, fromLineNumber: Math.min(...currentMultiResult), side });
                            return;
                        }
                    }
                    if (isUnifiedMode && (otherMultiResult === null || otherMultiResult === void 0 ? void 0 : otherMultiResult.length)) {
                        const max = Math.max(...otherMultiResult);
                        const diffFile = getDiffFile();
                        const index = (diffFile === null || diffFile === void 0 ? void 0 : diffFile.getUnifiedLineIndexByLineNumber(lineNum, side)) || -1;
                        const unifiedItem = (_b = diffFile === null || diffFile === void 0 ? void 0 : diffFile.getUnifiedLine) === null || _b === void 0 ? void 0 : _b.call(diffFile, index);
                        const otherSideLineNum = side === SplitSide.old ? unifiedItem === null || unifiedItem === void 0 ? void 0 : unifiedItem.newLineNumber : unifiedItem === null || unifiedItem === void 0 ? void 0 : unifiedItem.oldLineNumber;
                        if (max === otherSideLineNum) {
                            const finalResult = { [otherSide]: otherMultiResult };
                            updateMultiResult(finalResult);
                            onAddWidgetClick === null || onAddWidgetClick === void 0 ? void 0 : onAddWidgetClick({
                                lineNumber: max,
                                fromLineNumber: Math.min(...otherMultiResult),
                                side: otherSide === "old" ? SplitSide.old : SplitSide.new,
                            });
                            return;
                        }
                    }
                    updateMultiResult(undefined);
                    onAddWidgetClick === null || onAddWidgetClick === void 0 ? void 0 : onAddWidgetClick({ lineNumber: lineNum, fromLineNumber: lineNum, side });
                }
                else {
                    updateMultiResult(undefined);
                    onAddWidgetClick === null || onAddWidgetClick === void 0 ? void 0 : onAddWidgetClick({ lineNumber: lineNum, fromLineNumber: lineNum, side });
                }
            }, renderWidgetLine: renderWidgetLine ? internalRenderWidgetLine : undefined }) }));
};
const DiffViewWithMultiSelect = forwardRef(InternalDiffViewWithMultiSelect);

export { DiffModeEnum, DiffView, DiffViewWithMultiSelect, version };
//# sourceMappingURL=index.mjs.map
